import { Renderer } from './Renderer';
import { GameLoop } from './GameLoop';
import { InputManager } from '@client/input/InputManager';
import { AssetLoader } from './AssetLoader';
import { GameWorld } from '@client/world/GameWorld';
import { t, setLocale, getCurrentLocale } from '@locales/index';
import { COLORS } from '@shared/constants';
import { TargetingSystem } from '@client/systems/TargetingSystem';
import { Vector2 } from '@shared/types';
import { PassiveTreeUI } from '@client/ui/PassiveTreeUI';
import { AudioManager, GAME_SOUNDS, getAudioManager } from '@client/audio/AudioManager';
import { SaveMenuUI } from '@client/ui/SaveMenuUI';
import { getSaveManager, SaveData } from '@client/save/SaveManager';
import { InventoryUI } from '@client/ui/InventoryUI';
import { CraftingUI } from '@client/ui/CraftingUI';
import { DialogHTML as DialogUI } from '@client/ui/html/DialogHTML';
import { QuestUI } from '@client/ui/QuestUI';
import { QuestManager } from '@shared/quests/Quest';
import { WorldMapUI } from '@client/ui/WorldMapUI';
import { MainMenuUI } from '@client/ui/MainMenuUI';
import { SkillCreator, isSkillV2, HotbarSkill } from '@shared/skills/SkillCreator';
import { SkillCreatorV2, CustomSkillV2 } from '@shared/skills/SkillCreatorV2';
import { SkillEditorUI } from '@client/ui/SkillEditorUI';
import { SkillEditorHTML } from '@client/ui/html/SkillEditorHTML';
import { InventoryHTML } from '@client/ui/html/InventoryHTML';
import { GameMenuHTML } from '@client/ui/html/GameMenuHTML';
import { PassiveTreeEditorHTML } from '@client/ui/html/PassiveTreeEditorHTML';
import { HudHTML } from '@client/ui/html/HudHTML';
import { Minimap } from '@client/ui/Minimap';
import { GameMenu } from '@client/ui/GameMenu';
import { WaypointPanel } from '@client/ui/WaypointPanel';
import { TownPortal } from '@client/effects/TownPortal';
import { getWaypointManager } from '@shared/world/WaypointManager';
import { ZoneType, ZONES } from '@shared/world/Zone';
import { PotionSystem } from '@client/systems/PotionSystem';
import { getScreenShake } from '@client/effects/ScreenShake';
import { getParticleSystem } from '@client/effects/ParticleSystem';
import { KEY_BINDINGS } from '@shared/constants/KeyBindings';
import { initTileSprites } from '@client/world/TileSprites';
import { clearTileCache } from '@client/world/IsometricMap';
import { createQuestRewardItem } from '@shared/items/Item';
import { debugLog, debugWarn, debugError } from '@shared/utils/Logger';
import { PropRegistry } from '@client/assets/props/PropRegistry';
import { NPC } from '@client/entities/NPC';
// LootFilter temporarily disabled - will be re-added later
import { getDamageNumbers } from '@client/systems/DamageNumbers';
import { VendorHTML as VendorUI } from '@client/ui/html/VendorHTML';
import { TutorialSystem } from '@client/systems/TutorialSystem';
import { DroppedItem } from '@client/entities/DroppedItem';

// Demo mode toggle - set to false for full game release
export const DEMO_MODE = true;

// Act 2+ zones blocked during demo mode
const DEMO_BLOCKED_ZONES: Set<string> = new Set([
  'ergenekon_girisi', 'firtina_ovasi', 'ates_magarasi', 'yersub_tahti',
  'karanlik_tunel', 'olum_nehri', 'kemik_ormani', 'lanetli_saray',
  'erlik_kapisi', 'cehennem_bahcesi', 'taht_odasi',
]);

// Pre-computed skill digit key names to avoid template literal allocation each frame
const SKILL_DIGIT_KEYS = ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6'];

// Zone-specific color tint values (static, never change at runtime)
const ZONE_COLORS: Record<string, string> = {
  // Act 1
  'otag_koyu': '180, 150, 50',
  'karanlik_orman': '0, 80, 0',
  'ruh_batakligi': '80, 0, 100',
  'erlik_tapinagi': '139, 0, 0',
  // Act 2
  'ergenekon_girisi': '100, 80, 40',
  'firtina_ovasi': '60, 70, 90',
  'ates_magarasi': '180, 50, 0',
  'yersub_tahti': '0, 100, 80',
  // Act 3
  'karanlik_tunel': '20, 20, 40',
  'olum_nehri': '60, 0, 60',
  'kemik_ormani': '120, 110, 60',
  'lanetli_saray': '100, 0, 20',
  // Act 4
  'erlik_kapisi': '80, 0, 0',
  'cehennem_bahcesi': '160, 30, 0',
  'taht_odasi': '40, 0, 0',
};

// Zone transition description text (static, never change at runtime)
const ZONE_DESCRIPTIONS: Record<string, string> = {
  // Act 1
  'otag_koyu': 'G\u00fcvenli bir s\u0131\u011f\u0131nak...',
  'karanlik_orman': 'G\u00f6lgeler aras\u0131nda tehlike gizleniyor...',
  'ruh_batakligi': 'Kay\u0131p ruhlar\u0131n mekan\u0131...',
  'erlik_tapinagi': 'Karanl\u0131\u011f\u0131n kalbi...',
  // Act 2
  'ergenekon_girisi': 'Efsanevi vadinin giri\u015fi...',
  'firtina_ovasi': 'G\u00f6k g\u00fcrlemesi hi\u00e7 dinmiyor...',
  'ates_magarasi': 'Ate\u015fin ve k\u00fcllerin diyar\u0131...',
  'yersub_tahti': 'Yer-Sub\u0027un kutsal mekan\u0131...',
  // Act 3
  'karanlik_tunel': 'Yeralt\u0131na inen sonsuz karanl\u0131k...',
  'olum_nehri': 'Kay\u0131p ruhlar\u0131n akt\u0131\u011f\u0131 nehir...',
  'kemik_ormani': 'Devasa kemiklerden olu\u015fan orman...',
  'lanetli_saray': 'Lanetli duvarlar aras\u0131nda...',
  // Act 4
  'erlik_kapisi': 'Yeralt\u0131 tanr\u0131s\u0131n\u0131n kap\u0131s\u0131...',
  'cehennem_bahcesi': 'Cehennemin \u00e7i\u00e7ekleri aras\u0131nda...',
  'taht_odasi': 'Son sava\u015f burada ba\u015fl\u0131yor...',
};

type GameState = 'loading' | 'menu' | 'playing' | 'paused' | 'dead';

export class Game {
  private canvas: HTMLCanvasElement;
  private renderer: Renderer;
  private gameLoop: GameLoop;
  private inputManager: InputManager;
  private assetLoader: AssetLoader;
  private isRunning: boolean = false;

  private state: GameState = 'loading';
  private world: GameWorld | null = null;
  private passiveTreeUI: PassiveTreeUI | null = null;
  private audioManager: AudioManager;
  private saveMenuUI: SaveMenuUI;
  private inventoryUI: InventoryUI | null = null;
  private craftingUI: CraftingUI | null = null;
  private dialogUI: DialogUI | null = null;
  private questUI: QuestUI | null = null;
  private questManager: QuestManager | null = null;
  private worldMapUI: WorldMapUI | null = null;
  private mainMenuUI: MainMenuUI;
  private skillEditorUI: SkillEditorUI | null = null;
  private skillEditorHTML: SkillEditorHTML | null = null;
  private inventoryHTML: InventoryHTML | null = null;
  private gameMenuHTML: GameMenuHTML | null = null;
  private passiveEditorHTML: PassiveTreeEditorHTML | null = null;
  private hudHTML: HudHTML | null = null;
  private gameMenu: GameMenu | null = null;
  private waypointPanel: WaypointPanel | null = null;
  private townPortal: TownPortal | null = null;
  private potionSystem: PotionSystem | null = null;
  private vendorUI: VendorUI | null = null;
  private tutorialSystem: TutorialSystem = new TutorialSystem();

  // Cached canvas bounding rect (invalidated on resize)
  private _cachedCanvasRect: DOMRect | null = null;

  // Zone transition
  private zoneTransitionActive: boolean = false;
  private zoneTransitionFade: number = 0;
  private zoneTransitionPhase: 'fade_out' | 'fade_in' = 'fade_out';
  private pendingZoneId: ZoneType | null = null;
  private forceZoneChange: boolean = false;
  private pendingReturnPortal: ZoneType | null = null;
  private isReturningViaPortal: boolean = false;

  // Stored zone state for return portal
  private storedZoneState: {
    zoneId: ZoneType;
    playerPosition: { x: number; y: number };
    enemies: any[];
    droppedItems: any[];
    mapSeed?: number;
  } | null = null;

  // Pending melee/projectile attack (damage applied when animation finishes)
  private pendingAttack: {
    skill: HotbarSkill;
    damage: number;
    targetPos: Vector2;
    isV2: boolean;
  } | null = null;
  private wasAttacking: boolean = false;

  // Screen damage flash
  private screenDamageFlash: number = 0;
  private lastPlayerHp: number = 0;

  // Loading screen animation
  private loadingAnimTime: number = 0;
  private loadingTipIndex: number = Math.floor(Math.random() * 12);
  private loadingTipTimer: number = 0;
  private loadingTipFade: number = 1;

  // Death screen animation
  private deathFadeIn: number = 0;
  private deathCountdown: number = 5; // 5 second respawn countdown
  private deathCountdownReady: boolean = false; // true when countdown reaches 0
  private deathCauseText: string = '';
  private deathGoldLost: number = 0;

  // Pause menu settings overlay
  private pauseSettingsOpen: boolean = false;
  private pauseSliderDrag: number | null = null; // 0=master, 1=music, 2=sfx

  // Auto-save system
  private autoSaveTimer: number = 0;
  private autoSaveInterval: number = 60; // Auto-save every 60 seconds
  private autoSaveIndicatorTimer: number = 0;
  private isAutoSaving: boolean = false;
  private lastPlayerLevel: number = 0;

  // Demo mode state
  private demoComplete: boolean = false;
  private demoFadeIn: number = 0;
  private playTime: number = 0; // Total play time in seconds

  // Cached zone atmosphere gradients (recreated only on canvas resize)
  private _cachedAtmoGrads: Map<string, CanvasGradient> = new Map();
  private _atmoGradW: number = 0;
  private _atmoGradH: number = 0;

  // Cached animated fog offscreen canvas (repainted every ~200ms, not every frame)
  private _fogCanvas: HTMLCanvasElement | null = null;
  private _fogCtx: CanvasRenderingContext2D | null = null;
  private _fogLastTime: number = 0;
  private _fogZone: string = '';

  // Cached damage vignette gradient (recreated only on canvas resize)
  private _cachedDmgVigGrad: CanvasGradient | null = null;
  private _dmgVigW: number = 0;
  private _dmgVigH: number = 0;

  // Quest marker update throttle (every 500ms instead of every frame)
  private _questMarkerTimer: number = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.renderer = new Renderer(canvas);
    this.gameLoop = new GameLoop(this.update.bind(this), this.render.bind(this));
    this.gameLoop.setErrorHandler((error) => {
      console.error('[Game] Fatal error caught:', error.message);
      this.renderErrorOverlay(error);
      // Attempt emergency auto-save
      try {
        if (this.world) this.saveGame(0);
      } catch { /* save failed, nothing we can do */ }
    });
    this.inputManager = new InputManager(canvas);
    this.assetLoader = new AssetLoader();
    this.audioManager = AudioManager.getInstance();
    this.saveMenuUI = new SaveMenuUI(canvas.width, canvas.height);
    this.mainMenuUI = new MainMenuUI(canvas.width, canvas.height);

    this.setupCanvas();
    this.setupEventListeners();
    this.setupAudio();
    this.setupSaveSystem();
    this.setupMainMenu();
  }

  private setupAudio(): void {
    // Register all game sounds
    this.audioManager.registerSounds(GAME_SOUNDS);

    // Resume audio context on first user interaction (fallback if autoplay blocked)
    const resumeAudio = () => {
      this.audioManager.resumeContext();
      // Retry menu music if it was blocked
      if (this.state === 'menu') {
        this.audioManager.playMusic('music_menu');
      }
      document.removeEventListener('click', resumeAudio);
      document.removeEventListener('keydown', resumeAudio);
      document.removeEventListener('touchstart', resumeAudio);
    };

    document.addEventListener('click', resumeAudio);
    document.addEventListener('keydown', resumeAudio);
    document.addEventListener('touchstart', resumeAudio);
  }

  private setupSaveSystem(): void {
    // Set save callback
    this.saveMenuUI.setSaveCallback((slot) => {
      this.saveGame(slot);
    });

    // Set load callback
    this.saveMenuUI.setLoadCallback((slot) => {
      this.loadGame(slot);
    });

    // Mouse events for save menu
    this.canvas.addEventListener('mousemove', (e) => {
      if (this.saveMenuUI.isVisible()) {
        const rect = this.getCanvasRect();
        this.saveMenuUI.handleMouseMove(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    this.canvas.addEventListener('click', (e) => {
      if (this.saveMenuUI.isVisible()) {
        const rect = this.getCanvasRect();
        this.saveMenuUI.handleClick(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });
  }

  private setupMainMenu(): void {
    // Mouse events for main menu
    this.canvas.addEventListener('mousemove', (e) => {
      if (this.state === 'menu') {
        const rect = this.getCanvasRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        this.mainMenuUI.handleMouseMove(mx, my);
        this.mainMenuUI.handleMouseDrag(mx, my);
      }
    });

    this.canvas.addEventListener('mousedown', (e) => {
      if (this.state === 'menu') {
        const rect = this.getCanvasRect();
        this.mainMenuUI.handleMouseDown(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    this.canvas.addEventListener('mouseup', () => {
      if (this.state === 'menu') {
        this.mainMenuUI.handleMouseUp();
      }
    });

    this.canvas.addEventListener('click', (e) => {
      if (this.state === 'menu') {
        const rect = this.getCanvasRect();
        this.mainMenuUI.handleClick(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Set callbacks
    this.mainMenuUI.setCallbacks({
      onNewGame: (className) => {
        this.startGameWithClass(className);
      },
      onLoadGame: (slot) => {
        this.loadGame(slot);
        this.state = 'playing';
      },
      onLoadAutoSave: () => {
        this.loadAutoSave();
      }
    });
  }

  private loadAutoSave(): void {
    try {
      const saveManager = getSaveManager();
      const saveData = saveManager.loadAutoSave();

      if (saveData) {
        this.applySaveData(saveData);
        debugLog('Game', ' Auto-save loaded');
      } else {
        debugWarn('Game', ' No valid auto-save found (may be corrupted or missing)');
      }
    } catch (e) {
      debugError('Game', ' Failed to load auto-save:', e);
      debugWarn('Game', ' Auto-save load failed unexpectedly. The save file may be corrupted.');
    }
  }

  private startGameWithClass(className: import('@shared/types').ClassName): void {
    this.world = new GameWorld(this.canvas.width, this.canvas.height, className);
    this.world.player.initLPCRenderer(this.assetLoader);
    NPC.initLPCRenderer(this.assetLoader);
    DroppedItem.setAssetLoader(this.assetLoader);
    this.passiveTreeUI = new PassiveTreeUI(
      this.world.player.passiveTree,
      this.canvas.width,
      this.canvas.height,
      () => this.world?.player.recalculateStats()
    );
    this.inventoryUI = new InventoryUI(
      this.world.player,
      this.canvas.width,
      this.canvas.height,
      this.assetLoader
    );
    this.craftingUI = new CraftingUI(
      this.world.player.inventory,
      this.canvas.width,
      this.canvas.height
    );
    this.dialogUI = new DialogUI();
    this.questUI = new QuestUI(this.canvas.width, this.canvas.height);
    this.questManager = new QuestManager();
    this.worldMapUI = new WorldMapUI();
    this.skillEditorHTML = new SkillEditorHTML(this.world.player);
    this.inventoryHTML = new InventoryHTML(this.world.player);
    this.gameMenuHTML = new GameMenuHTML(this.world.player);
    this.passiveEditorHTML = new PassiveTreeEditorHTML();
    this.hudHTML = new HudHTML();
    this.hudHTML.show();
    this.hudHTML.setOpenPassiveCallback(() => {
      this.gameMenuHTML?.open('passives');
    });
    // Attach Canvas Minimap for high-quality terrain rendering
    const minimap = new Minimap(this.world.player);
    minimap.setMap(this.world.map);
    minimap.setScreenSize(this.canvas.width, this.canvas.height);
    this.hudHTML.setCanvasMinimap(minimap);
    this.skillEditorUI = new SkillEditorUI(
      this.world.player,
      this.canvas.width,
      this.canvas.height
    );
    this.gameMenu = new GameMenu(
      this.world.player,
      this.canvas.width,
      this.canvas.height,
      this.assetLoader
    );
    this.waypointPanel = new WaypointPanel(this.canvas.width, this.canvas.height);
    this.townPortal = new TownPortal();
    this.potionSystem = new PotionSystem();
    this.vendorUI = new VendorUI();
    this.setupPassiveTreeEvents();
    this.setupInventoryEvents();
    this.setupCraftingEvents();
    this.setupDialogEvents();
    this.setupQuestEvents();
    this.setupSkillEditorEvents();
    this.setupGameMenuEvents();
    this.setupWaypointEvents();
    this.setupVendorEvents();

    // Initialize auto-save tracking
    this.lastPlayerLevel = this.world.player.level;
    this.autoSaveTimer = 0;

    this.state = 'playing';
    // Switch to gameplay music
    this.audioManager.playMusic('music_gameplay');
    debugLog('Game',` Game started with class: ${className}`);

    // Trigger movement tutorial hint on new game start
    this.tutorialSystem.triggerHint('movement');
  }

  private saveGame(slot: number): void {
    if (!this.world) return;

    try {
      const saveManager = getSaveManager();
      const player = this.world.player;

      const saveData = saveManager.createSaveData({
        player: {
          className: player.className,
          level: player.level,
          experience: player.experience,
          skillPoints: player.skillPoints,
          hp: player.stats.hp,
          maxHp: player.stats.maxHp,
          mana: player.stats.mana,
          maxMana: player.stats.maxMana,
          positionX: player.position.x,
          positionY: player.position.y,
        },
        inventory: {
          gold: player.inventory.getGold(),
          items: player.inventory.serializeItems(),
          equipment: player.inventory.serializeEquipment(),
          effectStones: player.inventory.getEffectStones().map(s => ({
            id: s.id,
            name: s.name,
            nameKey: s.nameKey,
            type: s.type,
            rarity: s.rarity,
            effect: { ...s.effect },
          })),
          potionBelt: player.potionBelt.serialize() as import('@client/save/SaveManager').SavedPotionBelt,
        },
        skills: player.serializeSkills(),
        passives: player.passiveTree.serialize(),
        currency: Object.fromEntries(player.currencyStash),
        progress: {
          totalKills: this.world.totalKills || 0,
          bossesDefeated: this.world.bossesDefeated || 0,
        },
        quests: this.questManager?.serialize(),
        currentZone: this.world.getCurrentZoneId(),
        waypoints: getWaypointManager().serialize(),
      });

      if (saveManager.save(slot, saveData)) {
        debugLog('Game', ` Game saved to slot ${slot}`);
      }
    } catch (e) {
      debugError('Game', ` Failed to save game to slot ${slot}:`, e);
    }
  }

  private loadGame(slot: number): void {
    try {
      const saveManager = getSaveManager();
      const saveData = saveManager.load(slot);

      if (saveData) {
        this.applySaveData(saveData);
        debugLog('Game', ` Game loaded from slot ${slot}`);
      } else {
        debugWarn('Game', ` Could not load save from slot ${slot} - save may be corrupted or empty`);
      }
    } catch (e) {
      debugError('Game', ` Failed to load game from slot ${slot}:`, e);
      debugWarn('Game', ' Load failed unexpectedly. The save file may be corrupted.');
    }
  }

  private applySaveData(saveData: SaveData): void {
    try {
      this.applySaveDataUnsafe(saveData);
    } catch (e) {
      debugError('Game', ' Failed to apply save data, starting fresh:', e);
      // Fallback: start a new game with the saved class
      this.startGameWithClass(saveData.player?.className || 'alp');
    }
  }

  private applySaveDataUnsafe(saveData: SaveData): void {
    // Create world with saved class
    this.world = new GameWorld(this.canvas.width, this.canvas.height, saveData.player.className);
    this.world.player.initLPCRenderer(this.assetLoader);
    NPC.initLPCRenderer(this.assetLoader);
    DroppedItem.setAssetLoader(this.assetLoader);

    // Create all UI components
    this.passiveTreeUI = new PassiveTreeUI(
      this.world.player.passiveTree,
      this.canvas.width,
      this.canvas.height,
      () => this.world?.player.recalculateStats()
    );
    this.inventoryUI = new InventoryUI(
      this.world.player,
      this.canvas.width,
      this.canvas.height,
      this.assetLoader
    );
    this.craftingUI = new CraftingUI(
      this.world.player.inventory,
      this.canvas.width,
      this.canvas.height
    );
    this.dialogUI = new DialogUI();
    this.questUI = new QuestUI(this.canvas.width, this.canvas.height);
    this.questManager = new QuestManager();
    this.worldMapUI = new WorldMapUI();
    this.skillEditorHTML = new SkillEditorHTML(this.world.player);
    this.inventoryHTML = new InventoryHTML(this.world.player);
    this.gameMenuHTML = new GameMenuHTML(this.world.player);
    this.passiveEditorHTML = new PassiveTreeEditorHTML();
    this.hudHTML = new HudHTML();
    this.hudHTML.show();
    this.hudHTML.setOpenPassiveCallback(() => {
      this.gameMenuHTML?.open('passives');
    });
    // Attach Canvas Minimap for high-quality terrain rendering
    const minimap = new Minimap(this.world.player);
    minimap.setMap(this.world.map);
    minimap.setScreenSize(this.canvas.width, this.canvas.height);
    this.hudHTML.setCanvasMinimap(minimap);
    this.skillEditorUI = new SkillEditorUI(
      this.world.player,
      this.canvas.width,
      this.canvas.height
    );
    this.gameMenu = new GameMenu(
      this.world.player,
      this.canvas.width,
      this.canvas.height,
      this.assetLoader
    );
    this.waypointPanel = new WaypointPanel(this.canvas.width, this.canvas.height);
    this.townPortal = new TownPortal();
    this.potionSystem = new PotionSystem();
    this.vendorUI = new VendorUI();

    // Setup all events
    this.setupPassiveTreeEvents();
    this.setupInventoryEvents();
    this.setupCraftingEvents();
    this.setupDialogEvents();
    this.setupQuestEvents();
    this.setupSkillEditorEvents();
    this.setupGameMenuEvents();
    this.setupWaypointEvents();
    this.setupVendorEvents();

    const player = this.world.player;

    // Restore player stats
    player.level = saveData.player.level;
    player.experience = saveData.player.experience;
    player.skillPoints = saveData.player.skillPoints;
    player.stats.hp = saveData.player.hp;
    player.stats.maxHp = saveData.player.maxHp;
    player.stats.mana = saveData.player.mana;
    player.stats.maxMana = saveData.player.maxMana;
    player.position.x = saveData.player.positionX;
    player.position.y = saveData.player.positionY;

    // Restore inventory gold
    player.inventory.addGold(saveData.inventory.gold - player.inventory.getGold());

    // Restore inventory items
    if (saveData.inventory.items) {
      player.inventory.deserializeItems(saveData.inventory.items);
    }

    // Restore equipment
    if (saveData.inventory.equipment) {
      player.inventory.deserializeEquipment(saveData.inventory.equipment);
    }

    // Restore effect stones
    if (saveData.inventory.effectStones && Array.isArray(saveData.inventory.effectStones)) {
      for (const saved of saveData.inventory.effectStones) {
        if (saved && saved.id && saved.type && saved.effect) {
          player.inventory.addEffectStone(saved as import('@shared/skills/SkillCreator').EffectStone);
        }
      }
    }

    // Restore potion belt
    if (saveData.inventory.potionBelt) {
      player.potionBelt.deserialize(saveData.inventory.potionBelt as any);
    }

    // Restore skills
    if (saveData.skills) {
      player.deserializeSkills(saveData.skills);
    }

    // Restore passives
    player.passiveTree.deserialize(saveData.passives as Parameters<typeof player.passiveTree.deserialize>[0]);

    // Restore currency stash
    if (saveData.currency) {
      player.currencyStash = new Map(Object.entries(saveData.currency));
    }

    // Restore progress
    if (saveData.progress) {
      this.world.totalKills = saveData.progress.totalKills || 0;
      this.world.bossesDefeated = saveData.progress.bossesDefeated || 0;
    }

    // Restore quest state
    if (saveData.quests && this.questManager) {
      this.questManager.deserialize(saveData.quests);
      // Update quest availability based on restored player level
      this.questManager.updateQuestAvailability(this.world.player.level);
      // Refresh NPC quest markers and HUD tracker
      this.updateNpcQuestMarkers();
      this.updateHudQuestTracker();
      this.updateQuestObjectiveMarkers();
    }

    // Restore waypoint state
    if (saveData.waypoints) {
      getWaypointManager().deserialize(saveData.waypoints);
    }

    // Restore zone (change to saved zone if not the default)
    if (saveData.currentZone && saveData.currentZone !== 'otag_koyu') {
      this.world.changeZone(saveData.currentZone as ZoneType);
      // Re-place player at saved position (changeZone might move them)
      player.position.x = saveData.player.positionX;
      player.position.y = saveData.player.positionY;
    }

    // Update last player level for auto-save tracking
    this.lastPlayerLevel = player.level;
    this.autoSaveTimer = 0;

    this.state = 'playing';
    // Switch to gameplay music
    this.audioManager.playMusic('music_gameplay');

    debugLog('Game',` Save data applied - Class: ${saveData.player.className}, Level: ${saveData.player.level}`);
  }

  /**
   * Auto-save the game
   */
  private autoSave(): void {
    if (!this.world || this.state !== 'playing') return;

    try {
      const saveManager = getSaveManager();
      const player = this.world.player;

      const saveData = saveManager.createSaveData({
        player: {
          className: player.className,
          level: player.level,
          experience: player.experience,
          skillPoints: player.skillPoints,
          hp: player.stats.hp,
          maxHp: player.stats.maxHp,
          mana: player.stats.mana,
          maxMana: player.stats.maxMana,
          positionX: player.position.x,
          positionY: player.position.y,
        },
        inventory: {
          gold: player.inventory.getGold(),
          items: player.inventory.serializeItems(),
          equipment: player.inventory.serializeEquipment(),
          effectStones: player.inventory.getEffectStones().map(s => ({
            id: s.id,
            name: s.name,
            nameKey: s.nameKey,
            type: s.type,
            rarity: s.rarity,
            effect: { ...s.effect },
          })),
          potionBelt: player.potionBelt.serialize() as import('@client/save/SaveManager').SavedPotionBelt,
        },
        skills: player.serializeSkills(),
        passives: player.passiveTree.serialize(),
        progress: {
          totalKills: this.world.totalKills,
          bossesDefeated: this.world.bossesDefeated,
        },
        quests: this.questManager?.serialize(),
        currentZone: this.world.getCurrentZoneId(),
        waypoints: getWaypointManager().serialize(),
      });

      if (saveManager.autoSave(saveData)) {
        // Show auto-save indicator
        this.isAutoSaving = true;
        this.autoSaveIndicatorTimer = 2; // Show for 2 seconds
      }
    } catch (e) {
      debugError('Game', ' Auto-save failed unexpectedly:', e);
    }
  }

  /**
   * Trigger auto-save on important events
   */
  private triggerAutoSave(reason: string): void {
    debugLog('Game',` Auto-save triggered: ${reason}`);
    this.autoSave();
  }

  private getCanvasRect(): DOMRect {
    if (!this._cachedCanvasRect) {
      this._cachedCanvasRect = this.canvas.getBoundingClientRect();
    }
    return this._cachedCanvasRect;
  }

  private setupCanvas(): void {
    const resize = () => {
      const container = this.canvas.parentElement;
      if (container) {
        const { width, height } = container.getBoundingClientRect();

        const aspectRatio = 16 / 9;
        let canvasWidth = width;
        let canvasHeight = width / aspectRatio;

        if (canvasHeight > height) {
          canvasHeight = height;
          canvasWidth = height * aspectRatio;
        }

        this.canvas.width = canvasWidth;
        this.canvas.height = canvasHeight;
        this.canvas.style.width = `${canvasWidth}px`;
        this.canvas.style.height = `${canvasHeight}px`;

        // Invalidate cached bounding rect after resize
        this._cachedCanvasRect = null;

        // Update UI screen sizes
        this.mainMenuUI.setScreenSize(canvasWidth, canvasHeight);
        this.saveMenuUI.setScreenSize(canvasWidth, canvasHeight);

        // Update game menu size if exists
        if (this.gameMenu) {
          this.gameMenu.resize(canvasWidth, canvasHeight);
        }

        // VendorHTML handles its own sizing via CSS

        // Update skill editor size if exists
        if (this.skillEditorUI) {
          this.skillEditorUI.resize(canvasWidth, canvasHeight);
        }

        // Update world screen size if exists
        if (this.world) {
          this.world.setScreenSize(canvasWidth, canvasHeight);
        }
      }
    };

    resize();
    window.addEventListener('resize', resize);
  }

  private setupEventListeners(): void {
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Dismiss tutorial hint on any mouse click
    this.canvas.addEventListener('mousedown', () => {
      this.tutorialSystem.dismiss();
    });

    // Pause menu click handling (settings button + settings panel)
    this.canvas.addEventListener('click', (e) => {
      if (this.state !== 'paused') return;
      const rect = this.getCanvasRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      this.handlePauseClick(mx, my);
    });

    // Pause menu slider drag
    this.canvas.addEventListener('mousedown', (e) => {
      if (this.state !== 'paused' || !this.pauseSettingsOpen) return;
      const rect = this.getCanvasRect();
      this.pauseSliderDrag = this.getPauseSliderAtPosition(e.clientX - rect.left, e.clientY - rect.top);
    });

    this.canvas.addEventListener('mousemove', (e) => {
      if (this.state === 'paused' && this.pauseSliderDrag !== null) {
        const rect = this.getCanvasRect();
        this.applyPauseSliderDrag(e.clientX - rect.left);
      }
    });

    this.canvas.addEventListener('mouseup', () => {
      if (this.pauseSliderDrag !== null) {
        this.pauseSliderDrag = null;
        this.persistPauseSettings();
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    });

    // Demo complete screen click handling
    this.canvas.addEventListener('click', (e) => {
      if (!this.demoComplete) return;
      const rect = this.getCanvasRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      this.handleDemoCompleteClick(mx, my);
    });

    // Language toggle with L key
    window.addEventListener('keydown', (e) => {
      // Dismiss tutorial hint on any keypress
      this.tutorialSystem.dismiss();

      // Language toggle
      if (e.code === KEY_BINDINGS.TOGGLE_LANGUAGE.code) {
        const newLocale = getCurrentLocale() === 'tr' ? 'en' : 'tr';
        setLocale(newLocale);
      }

      // Vendor UI input handling (takes priority over dialog)
      // VendorHTML and DialogHTML handle their own keyboard events via DOM
      if (this.vendorUI?.isOpen()) return;
      if (this.dialogUI?.isVisible()) return;

      // Quest UI input handling
      if (this.questUI?.isVisible()) {
        if (this.questUI.handleKeyDown(e.key)) {
          return;
        }
      }

      // Escape to toggle game menu or close other menus
      // Note: Minimap is NOT closed by ESC - only TAB toggles it
      if (e.code === 'Escape') {
        // VendorHTML and DialogHTML handle ESC internally
        if (this.waypointPanel?.isOpen()) {
          this.waypointPanel.close();
        } else if (this.questUI?.isVisible()) {
          this.questUI.close();
        } else if (this.craftingUI?.isVisible()) {
          this.craftingUI.close();
        } else if (this.inventoryHTML?.isVisible()) {
          this.inventoryHTML.close();
        } else if (this.inventoryUI?.isVisible()) {
          this.inventoryUI.close();
        } else if (this.passiveTreeUI?.isVisible()) {
          this.passiveTreeUI.close();
        } else if (this.skillEditorHTML?.isVisible()) {
          this.skillEditorHTML.close();
        } else if (this.skillEditorUI?.isVisible()) {
          this.skillEditorUI.close();
        } else if (this.saveMenuUI.isVisible()) {
          this.saveMenuUI.close();
        } else if (this.worldMapUI?.isVisible()) {
          this.worldMapUI.close();
        } else if (this.gameMenuHTML?.isVisible()) {
          this.gameMenuHTML.close();
        } else if (this.gameMenu?.isVisible()) {
          this.gameMenu.close();
        } else if (this.state === 'playing') {
          // ESC opens HTML game menu
          this.gameMenuHTML?.open();
        }
      }

      // F8 to open passive tree editor (dev tool)
      if (e.code === 'F8') {
        this.passiveEditorHTML?.toggle();
        e.preventDefault();
        return;
      }

      // K key to open/close skill editor (HTML version) - BEFORE menu shortcuts
      if (e.code === 'KeyK' && this.state === 'playing'
        && !this.gameMenu?.isVisible()
        && !this.vendorUI?.isOpen()
        && !this.dialogUI?.isVisible()
      ) {
        if (this.skillEditorHTML?.isVisible()) {
          this.skillEditorHTML.close();
        } else {
          this.skillEditorHTML?.open();
        }
        e.preventDefault();
        return;
      }

      // I key to open/close HTML inventory - BEFORE menu shortcuts
      if (e.code === 'KeyI' && this.state === 'playing'
        && !this.gameMenu?.isVisible()
        && !this.vendorUI?.isOpen()
        && !this.dialogUI?.isVisible()
        && !this.skillEditorHTML?.isVisible()
      ) {
        this.inventoryHTML?.toggle();
        e.preventDefault();
        return;
      }

      // Menu shortcuts - open unified GameMenu with specific tab (K,I excluded - handled above)
      if (this.state === 'playing' && e.code !== 'KeyK' && e.code !== 'KeyI') {
        const tabId = this.gameMenu?.handleShortcut(e.key);
        if (tabId) {
          this.gameMenu?.toggle(tabId);
          e.preventDefault();
          return;
        }
      }

      // E key to toggle edit mode (when passive tree is open)
      if (e.code === 'KeyE' && this.passiveTreeUI?.isVisible()) {
        this.passiveTreeUI.toggleEditMode();
        e.preventDefault();
        return;
      }

      // E key to interact with nearby NPC (when playing and no UI is open)
      if (e.code === 'KeyE' && this.state === 'playing'
        && !this.vendorUI?.isOpen()
        && !this.dialogUI?.isVisible()
        && !this.passiveTreeUI?.isVisible()
        && !this.gameMenu?.isVisible()
        && !this.inventoryUI?.isVisible()
        && !this.craftingUI?.isVisible()
        && !this.skillEditorUI?.isVisible()
      ) {
        const nearbyNPC = this.world?.getHighlightedNPC();
        if (nearbyNPC) {
          e.preventDefault();

          // Quest: talk_to_npc progress
          if (this.questManager) {
            this.questManager.updateProgress('talk_to_npc', nearbyNPC.id, 1);
          }

          // Build quest dialog if there are quest interactions available
          if (this.questManager && this.dialogUI) {
            const availableQuests = this.questManager.getAvailableQuests()
              .filter(q => q.giverNpcId === nearbyNPC.id);
            const completableQuests = this.questManager.getActiveQuests()
              .filter(q => {
                const allDone = q.objectives.every(o => o.completed);
                return allDone && (!q.turnInNpcId || q.turnInNpcId === nearbyNPC.id) &&
                  q.giverNpcId === nearbyNPC.id;
              });
            const activeQuests = this.questManager.getActiveQuests()
              .filter(q => q.giverNpcId === nearbyNPC.id && !q.objectives.every(o => o.completed));

            const questDialog = nearbyNPC.buildQuestDialog(availableQuests, completableQuests, activeQuests);

            if (questDialog) {
              this.dialogUI.openWithDialog(nearbyNPC, questDialog);
            } else {
              this.dialogUI.open(nearbyNPC);
            }
          } else {
            this.dialogUI?.open(nearbyNPC);
          }

          return;
        }
      }

      // X key to export positions (when passive tree is in edit mode)
      if (e.code === 'KeyX' && this.passiveTreeUI?.isVisible() && this.passiveTreeUI?.isEditModeActive()) {
        this.passiveTreeUI.exportPositions();
        e.preventDefault();
        return;
      }

      // Inventory
      if (e.code === KEY_BINDINGS.MENU_INVENTORY.code && this.state === 'playing') {
        this.inventoryHTML?.toggle();
      }

      // Crafting
      if (e.code === KEY_BINDINGS.MENU_CRAFTING.code && this.state === 'playing') {
        this.craftingUI?.toggle();
      }

      // Quest log
      if (e.code === KEY_BINDINGS.MENU_QUESTS.code && this.state === 'playing') {
        this.questUI?.toggle();
      }

      // Minimap toggle (HTML HUD minimap)
      if (e.code === KEY_BINDINGS.MINIMAP_TOGGLE.code && this.state === 'playing') {
        e.preventDefault();
        if (this.hudHTML) {
          this.hudHTML.toggleMinimap();
        }
      }

      // Health potion
      if (e.code === KEY_BINDINGS.POTION_HEALTH.code && this.state === 'playing' && this.world) {
        // Track potion count before use for empty warning
        const beltSlot0 = this.world.player.potionBelt.slots[0];
        const prevHealthCount = beltSlot0 ? beltSlot0.count : 0;

        // Try PotionBelt slot 0 first (new consumable system)
        const beltEffect = this.world.player.potionBelt.usePotion(0);
        if (beltEffect) {
          this.world.player.applyPotionEffect(beltEffect);
          this.audioManager.play('item_pickup');
          // Quick green flash for health potion feedback
          getScreenShake().triggerFlash('#44ff44', 0.15, 0.15);
          debugLog('Potion', ' Used potion belt slot 0');

          // Check if potion slot is now empty (count was > 0 and now slot is null or count is 0)
          const afterSlot = this.world.player.potionBelt.slots[0];
          if (prevHealthCount > 0 && (!afterSlot || afterSlot.count <= 0)) {
            getDamageNumbers().spawn(
              this.world.player.position.x,
              this.world.player.position.y - 30,
              t('combat.potionEmpty'),
              'miss'
            );
          }
        } else if (this.potionSystem && this.potionSystem.canUseHealthPotion()) {
          // Fall back to charge-based health potion
          const healAmount = this.potionSystem.useHealthPotion(this.world.player.level);
          if (healAmount > 0) {
            this.world.player.heal(healAmount);
            this.audioManager.play('item_pickup');
            debugLog('Potion',` Used health potion, healed ${healAmount} HP`);
          }
        }
      }

      // Mana potion
      if (e.code === KEY_BINDINGS.POTION_MANA.code && this.state === 'playing' && this.world) {
        // Track potion count before use for empty warning
        const beltSlot1 = this.world.player.potionBelt.slots[1];
        const prevManaCount = beltSlot1 ? beltSlot1.count : 0;

        // Try PotionBelt slot 1 first (new consumable system)
        const beltEffect = this.world.player.potionBelt.usePotion(1);
        if (beltEffect) {
          this.world.player.applyPotionEffect(beltEffect);
          this.audioManager.play('item_pickup');
          // Quick blue flash for mana potion feedback
          getScreenShake().triggerFlash('#4488ff', 0.15, 0.15);
          debugLog('Potion', ' Used potion belt slot 1');

          // Check if potion slot is now empty
          const afterSlot = this.world.player.potionBelt.slots[1];
          if (prevManaCount > 0 && (!afterSlot || afterSlot.count <= 0)) {
            getDamageNumbers().spawn(
              this.world.player.position.x,
              this.world.player.position.y - 30,
              t('combat.potionEmpty'),
              'miss'
            );
          }
        } else if (this.potionSystem && this.potionSystem.canUseManaPotion()) {
          // Fall back to charge-based mana potion
          const manaAmount = this.potionSystem.useManaPotion(this.world.player.level);
          if (manaAmount > 0) {
            this.world.player.stats.mana = Math.min(
              this.world.player.stats.maxMana,
              this.world.player.stats.mana + manaAmount
            );
            this.audioManager.play('item_pickup');
            debugLog('Potion',` Used mana potion, restored ${manaAmount} Mana`);
          }
        }
      }

      // Zone transition / Waypoint interaction / Interact
      if (e.code === KEY_BINDINGS.INTERACT.code && this.state === 'playing' && this.world && !this.zoneTransitionActive) {
        // Check waypoint interaction first
        const waypoint = this.world.getWaypoint();
        if (waypoint && waypoint.isPlayerNear(this.world.player.position.x, this.world.player.position.y)) {
          const currentZone = this.world.getCurrentZoneId() as ZoneType;
          const waypointManager = getWaypointManager();
          if (!waypoint.isActivated()) {
            // Activate the waypoint
            const activated = waypointManager.activateWaypoint(currentZone);
            if (activated) {
              waypoint.setActivated(true);
              this.audioManager.play('ui_level_up', { volume: 0.6 });
              getDamageNumbers().spawn(
                this.world.player.position.x,
                this.world.player.position.y - 40,
                t('waypoint.activated'),
                'level_up'
              );
              // Waypoint activation particle burst
              const ps = getParticleSystem();
              if (ps) {
                ps.emit({
                  position: { x: this.world.player.position.x, y: this.world.player.position.y },
                  count: 20,
                  color: ['#44ddff', '#88eeff', '#ffffff'],
                  speed: { min: 50, max: 180 },
                  size: { min: 2, max: 4 },
                  life: { min: 0.4, max: 1.0 },
                  direction: -Math.PI / 2,
                  spread: Math.PI,
                  gravity: -30,
                  friction: 2,
                  alphaDecay: 0.8,
                  sizeDecay: 2,
                  shape: 'star',
                });
              }
              getScreenShake().triggerFlash('#44ddff', 0.2, 0.2);
            }
          } else {
            // Open waypoint panel for teleportation
            if (this.waypointPanel) {
              this.waypointPanel.open(currentZone);
            }
          }
        } else {
          // Check zone transition
          const transition = this.world.checkZoneTransition();
          if (transition.canTransition && transition.exit) {
            // Demo mode: block transitions to Act 2+ zones
            if (DEMO_MODE && DEMO_BLOCKED_ZONES.has(transition.exit.targetZone)) {
              getDamageNumbers().spawn(
                this.world.player.position.x,
                this.world.player.position.y - 40,
                t('demo_zone_blocked'),
                'miss'
              );
            } else {
              this.startZoneTransition(transition.exit.targetZone);
            }
          } else if (transition.reason) {
            // Show blocked reason to player
            getDamageNumbers().spawn(
              this.world.player.position.x,
              this.world.player.position.y - 40,
              transition.reason,
              'miss'
            );
          }
        }
      }

      // World map (info only - travel via waypoints)
      if (e.code === KEY_BINDINGS.MENU_MAP.code && this.state === 'playing') {
        if (this.world && this.worldMapUI) {
          this.worldMapUI.toggle(this.world.getCurrentZone().id, this.world.player.level);
        }
      }

      // Town portal (only works in non-town zones)
      if (e.code === KEY_BINDINGS.TOWN_PORTAL.code && this.state === 'playing' && this.world && !this.zoneTransitionActive) {
        // Close waypoint panel if open
        if (this.waypointPanel?.isOpen()) {
          this.waypointPanel.close();
        }

        const waypointManager = getWaypointManager();
        const currentZone = this.world.getCurrentZoneId() as ZoneType;

        // Don't open portal if already in town
        if (waypointManager.isTown(currentZone)) {
          return;
        }

        // Toggle portal - if already open, close it
        if (this.townPortal?.isTownPortalActive()) {
          this.townPortal.closeTownPortal();
          waypointManager.closePortal();
          return;
        }

        const lastTown = waypointManager.getLastTown();
        if (lastTown && this.townPortal) {
          // Open visual portal near player
          const player = this.world.player;
          this.townPortal.openTownPortal(player.position.x + 60, player.position.y, lastTown);

          // Update waypoint manager state
          waypointManager.openTownPortal(currentZone);

          debugLog('Portal', ' T pressed - portal opened');
        }
      }

      // Toggle mute
      if (e.code === KEY_BINDINGS.TOGGLE_MUTE.code) {
        this.audioManager.toggleMute();
      }

      // Save/Load keys
      if (this.state === 'playing') {
        // Quick Save
        if (e.code === KEY_BINDINGS.QUICK_SAVE.code) {
          e.preventDefault();
          this.saveGame(getSaveManager().getCurrentSlot());
        }

        // Quick Load
        if (e.code === KEY_BINDINGS.QUICK_LOAD.code) {
          e.preventDefault();
          this.loadGame(getSaveManager().getCurrentSlot());
        }

        // Save Menu
        if (e.code === KEY_BINDINGS.SAVE_MENU.code) {
          e.preventDefault();
          this.saveMenuUI.toggle('save');
        }

        // Load Menu
        if (e.code === KEY_BINDINGS.LOAD_MENU.code) {
          e.preventDefault();
          this.saveMenuUI.toggle('load');
        }
      }

      // Close save menu with Escape
      if (this.saveMenuUI.isVisible() && e.code === 'Escape') {
        this.saveMenuUI.close();
      }

      // Close pause settings with Escape, or resume from pause
      if (this.state === 'paused' && e.code === 'Escape') {
        if (this.pauseSettingsOpen) {
          this.pauseSettingsOpen = false;
        } else {
          this.resume();
        }
      }

      // Restart game from death screen (only after countdown finishes)
      if (this.state === 'dead' && this.deathCountdownReady && (e.code === 'Space' || e.code === 'Enter')) {
        this.respawnInTown();
      }
    });

  }

  async start(): Promise<void> {
    debugLog('Game',` Starting... (${getCurrentLocale()})`);

    try {
      // Initialize image-based props (loads sprites in background, procedural fallback until ready)
      PropRegistry.initImageProps();

      // Initialize tile sprites (loads in background, procedural terrain buffer as base layer)
      initTileSprites(() => clearTileCache());

      await this.assetLoader.loadInitialAssets();
    } catch (e) {
      debugLog('Game', ' Asset loading failed, continuing with fallbacks:', e);
    }

    this.state = 'menu';
    this.isRunning = true;
    this.gameLoop.start();

    // Try to play menu music immediately (may be blocked by browser)
    this.audioManager.playMusic('music_menu');
  }

  private setupDialogEvents(): void {
    // Dialog close callback
    if (this.dialogUI) {
      this.dialogUI.setOnClose(() => {
        // Resume game when dialog closes
      });

      // Shop open callback - triggered when player selects a "dukkan" dialog option
      this.dialogUI.setOnShopOpen((npc: NPC) => {
        if (this.vendorUI && this.world) {
          this.vendorUI.open(npc, this.world.player.inventory);
          debugLog('Vendor', ` Shop opened for NPC: ${npc.name} (${npc.type})`);
        }
      });

      // Quest action callback from dialog options
      this.dialogUI.setOnQuestAction((action) => {
        if (!this.questManager) return;

        if (action.type === 'accept_quest') {
          const accepted = this.questManager.acceptQuest(action.questId);
          if (accepted) {
            const quest = this.questManager.getQuest(action.questId);
            debugLog('Dialog',` Quest accepted: ${quest?.name}`);
            // Auto-track newly accepted quest
            if (this.questUI) {
              this.questUI.trackQuest(action.questId);
            }
            // Play quest accept sound
            this.audioManager.play('ui_confirm');
          }
        } else if (action.type === 'complete_quest') {
          const rewards = this.questManager.completeQuest(action.questId);
          if (rewards) {
            const quest = this.questManager.getQuest(action.questId);
            debugLog('Dialog',` Quest completed via dialog: ${quest?.name}`);
          }
        }

        this.updateNpcQuestMarkers();
        this.updateHudQuestTracker();
        this.updateQuestObjectiveMarkers();
      });
    }
  }

  private setupVendorEvents(): void {
    // VendorHTML handles all mouse/keyboard events internally via DOM
  }

  private setupQuestEvents(): void {
    if (!this.questManager || !this.world) return;

    // Update quest availability based on player level
    this.questManager.updateQuestAvailability(this.world.player.level);

    // Quest update callback
    this.questManager.setOnQuestUpdate((quest) => {
      debugLog('Quest',` Updated: ${quest.name}`);
      if (this.questUI) {
        this.questUI.updateQuests(this.questManager!.getAllQuests());
      }
      this.updateNpcQuestMarkers();
      this.updateHudQuestTracker();
      this.updateQuestObjectiveMarkers();
    });

    // Quest complete callback
    this.questManager.setOnQuestComplete((quest) => {
      debugLog('Quest',` Completed: ${quest.name}`);
      // Give rewards
      if (this.world && quest.rewards) {
        if (quest.rewards.exp) {
          this.world.player.addExperience(quest.rewards.exp);
          debugLog('Quest',`[Reward] +${quest.rewards.exp} XP`);
        }
        if (quest.rewards.gold) {
          this.world.player.inventory.addGold(quest.rewards.gold);
          debugLog('Quest',`[Reward] +${quest.rewards.gold} Gold`);
        }
        if (quest.rewards.skillPoints) {
          this.world.player.skillPoints += quest.rewards.skillPoints;
          debugLog('Quest',`[Reward] +${quest.rewards.skillPoints} Skill Points`);
        }
        if (quest.rewards.passivePoints) {
          for (let i = 0; i < quest.rewards.passivePoints; i++) {
            this.world.player.passiveTree.addPoint();
          }
          debugLog('Quest',`[Reward] +${quest.rewards.passivePoints} Passive Points`);
        }
        if (quest.rewards.itemIds) {
          for (const itemId of quest.rewards.itemIds) {
            const rewardItem = createQuestRewardItem(itemId);
            if (rewardItem) {
              this.world.player.pickupItem(rewardItem);
              debugLog('Quest',`[Reward] Item: ${rewardItem.name}`);
            }
          }
        }

        // Show quest completion notification + audio + celebration
        this.audioManager.play('ui_level_up');
        // Quest completion notification in HudHTML
        if (this.hudHTML?.isShown()) {
          const rewardParts: string[] = [];
          if (quest.rewards?.exp) rewardParts.push(`+${quest.rewards.exp} XP`);
          if (quest.rewards?.gold) rewardParts.push(`+${quest.rewards.gold} Altin`);
          if (quest.rewards?.passivePoints) rewardParts.push(`+${quest.rewards.passivePoints} Pasif Puan`);
          this.hudHTML.showQuestComplete(quest.name, rewardParts.join('  '));
        }
        getScreenShake().shake({ intensity: 5, duration: 0.3 });
        getParticleSystem().emitLevelUp(
          this.world.player.position.x,
          this.world.player.position.y
        );
      }
      // Demo mode: trigger demo complete screen when Act 1 boss quest is completed
      if (DEMO_MODE && quest.id === 'act1_boss') {
        this.demoComplete = true;
        this.demoFadeIn = 0;
        this.audioManager.play('ui_level_up');
        debugLog('Game', 'Demo complete! Act 1 boss defeated.');
      }

      // Update UI
      if (this.questUI) {
        this.questUI.updateQuests(this.questManager!.getAllQuests());
      }
      // Trigger celebration effect in QuestsTab
      if (this.gameMenu) {
        const questsTab = this.gameMenu.getQuestsTab();
        if (questsTab) {
          questsTab.updateQuests(this.questManager!.getAllQuests());
          questsTab.triggerCelebration(quest.id);
        }
      }
      this.updateHudQuestTracker();
      this.updateQuestObjectiveMarkers();
      // Auto-save on quest completion
      this.triggerAutoSave('quest_complete');
    });

    // Initial UI update
    if (this.questUI) {
      this.questUI.updateQuests(this.questManager.getAllQuests());
    }

    // Auto-accept first quest (only if not already active or completed)
    const introQuest = this.questManager.getQuest('act1_intro');
    if (introQuest && introQuest.status !== 'active' && introQuest.status !== 'completed') {
      this.questManager.acceptQuest('act1_intro');
    }
    this.updateHudQuestTracker();
    this.updateQuestObjectiveMarkers();

    // Setup world callbacks for quest tracking and potion charges
    this.world.setOnEnemyKilled((enemyType, isBoss) => {
      if (this.questManager) {
        if (isBoss) {
          this.questManager.updateProgress('kill_boss', enemyType, 1);
        }
        this.questManager.updateProgress('kill_enemy', enemyType, 1);
      }

      // Restore potion charges on kill
      if (this.potionSystem) {
        // Rare enemies: al_kormos, albasti, erlik_okcusu, erlik_guard, etc.
        const rareEnemies = ['al_kormos', 'albasti', 'erlik_okcusu', 'erlik_guard',
          'dag_devi', 'ates_iblisi', 'golge_avcisi', 'olum_habercisi',
          'erlik_sovalyesi', 'karanlik_seraf'];
        const isRare = rareEnemies.includes(enemyType);
        this.potionSystem.onEnemyKill(isRare, isBoss);
      }
    });

    this.world.setOnZoneEntered((zoneId) => {
      if (this.questManager) {
        this.questManager.updateProgress('reach_zone', zoneId, 1);
        this.questManager.updateQuestAvailability(this.world!.player.level);
      }
    });

    // Item collected callback for collect_item quest objectives
    this.world.setOnItemCollected((itemId: string) => {
      if (this.questManager) {
        this.questManager.updateProgress('collect_item', itemId, 1);
      }
    });

    // Quest active check callback (for conditional loot drops)
    this.world.setIsQuestActive((questId: string) => {
      if (!this.questManager) return false;
      const quest = this.questManager.getQuest(questId);
      return quest?.status === 'active';
    });

    // Initial NPC quest marker update
    this.updateNpcQuestMarkers();
  }

  private updateNpcQuestMarkers(): void {
    if (!this.questManager || !this.world) return;
    for (const npc of this.world.npcs) {
      npc.questMarkerType = this.questManager.getNpcQuestMarker(npc.id);
    }
  }

  private updateHudQuestTracker(): void {
    if (!this.questManager || !this.world) return;
    const activeQuests = this.questManager.getActiveQuests();
    if (activeQuests.length === 0) {
      if (this.hudHTML?.isShown()) {
        this.hudHTML.setTrackedQuest(null);
      }
      return;
    }

    // Prefer the quest explicitly tracked in QuestUI, then fall back to first active
    const trackedId = this.questUI?.getTrackedQuestId();
    let quest = trackedId
      ? activeQuests.find(q => q.id === trackedId)
      : null;

    // If tracked quest is no longer active, auto-track the first active quest
    if (!quest) {
      quest = activeQuests[0];
      // Auto-track the first active quest in the QuestUI as well
      if (this.questUI && quest) {
        this.questUI.trackQuest(quest.id);
      }
    }

    // Feed quest info to HudHTML
    if (this.hudHTML?.isShown() && quest) {
      this.hudHTML.setTrackedQuest({
        name: quest.name,
        objectives: quest.objectives.map(o => ({
          description: o.description,
          current: o.current,
          required: o.required,
          completed: o.completed,
        })),
      });
    }
  }

  private updateQuestObjectiveMarkers(): void {
    if (!this.questManager || !this.world) return;

    const locations: Array<{ x: number; y: number; label?: string }> = [];
    const activeQuests = this.questManager.getActiveQuests();

    for (const quest of activeQuests) {
      for (const obj of quest.objectives) {
        if (obj.completed) continue;

        // For talk_to_npc objectives, find the NPC position
        if (obj.type === 'talk_to_npc') {
          const npc = this.world.npcs.find(n => n.id === obj.target);
          if (npc) {
            locations.push({
              x: npc.position.x,
              y: npc.position.y,
              label: obj.description,
            });
          }
        }

        // For turn-in quests where all objectives are done, mark the turn-in NPC
        const allObjsDone = quest.objectives.every(o => o.completed || o === obj);
        if (allObjsDone && quest.turnInNpcId) {
          const turnInNpc = this.world.npcs.find(n => n.id === quest.turnInNpcId);
          if (turnInNpc) {
            locations.push({
              x: turnInNpc.position.x,
              y: turnInNpc.position.y,
              label: quest.name,
            });
          }
        }
      }
    }

    this.world.setQuestObjectiveLocations(locations);
  }

  private startZoneTransition(targetZoneId: ZoneType, force: boolean = false): void {
    // Boss arena lock: prevent zone transition during boss fights
    if (!force && this.world?.isBossActive()) {
      getDamageNumbers().spawn(
        this.world.player.position.x,
        this.world.player.position.y - 40,
        t('boss_arena_locked'),
        'miss'
      );
      return;
    }
    this.zoneTransitionActive = true;
    this.zoneTransitionPhase = 'fade_out';
    this.zoneTransitionFade = 0;
    this.pendingZoneId = targetZoneId;
    this.forceZoneChange = force;
    this.audioManager.play('ui_confirm');
    debugLog('Game',` Starting zone transition to: ${targetZoneId}${force ? ' (forced)' : ''}`);
  }

  private updateZoneTransition(deltaTime: number): void {
    if (!this.zoneTransitionActive) return;

    const fadeSpeed = 1.25; // Fade duration ~0.8s (more cinematic)

    if (this.zoneTransitionPhase === 'fade_out') {
      this.zoneTransitionFade += deltaTime * fadeSpeed;
      if (this.zoneTransitionFade >= 1) {
        this.zoneTransitionFade = 1;
        // Execute zone change
        if (this.world && this.pendingZoneId) {
          // Check if we're returning via portal and have stored state
          if (this.isReturningViaPortal && this.storedZoneState &&
              this.storedZoneState.zoneId === this.pendingZoneId) {
            // Change zone WITHOUT spawning new enemies
            this.world.changeZone(this.pendingZoneId!, true, this.forceZoneChange);

            // Restore the stored enemies and items
            this.world.restoreZoneState(this.storedZoneState);

            // Restore player position
            this.world.player.position.x = this.storedZoneState.playerPosition.x;
            this.world.player.position.y = this.storedZoneState.playerPosition.y;

            debugLog('Portal', ' Restored zone state for:', this.pendingZoneId);

            // Clear stored state
            this.clearStoredZoneState();
          } else {
            // Normal zone change - regenerate zone
            this.world.changeZone(this.pendingZoneId!, false, this.forceZoneChange);
          }

          // Reset return flag
          this.isReturningViaPortal = false;

          // Spawn return portal if we arrived via town portal
          if (this.pendingReturnPortal && this.townPortal) {
            // Spawn return portal next to the waypoint
            const zoneConfig = this.world.getCurrentZone();
            if (zoneConfig.waypointPosition) {
              // Move player near the waypoint when arriving via town portal
              this.world.player.position.x = zoneConfig.waypointPosition.x - 50;
              this.world.player.position.y = zoneConfig.waypointPosition.y;

              // Open return portal next to waypoint (on the right side)
              this.townPortal.openReturnPortal(
                zoneConfig.waypointPosition.x + 80,
                zoneConfig.waypointPosition.y,
                this.pendingReturnPortal
              );
              debugLog('Portal', ' Return portal opened at', zoneConfig.waypointPosition.x + 80, zoneConfig.waypointPosition.y);
            }
            this.pendingReturnPortal = null;
          }
        }
        // Update minimap with new map after zone change
        if (this.world && this.hudHTML) {
          const minimap = new Minimap(this.world.player);
          minimap.setMap(this.world.map);
          minimap.setScreenSize(this.canvas.width, this.canvas.height);
          this.hudHTML.setCanvasMinimap(minimap);
        }
        // Play zone-specific music
        if (this.world) {
          this.audioManager.playZoneMusic(this.world.getCurrentZoneId());
        }
        this.zoneTransitionPhase = 'fade_in';
      }
    } else {
      this.zoneTransitionFade -= deltaTime * fadeSpeed;
      if (this.zoneTransitionFade <= 0) {
        this.zoneTransitionFade = 0;
        this.zoneTransitionActive = false;
        this.pendingZoneId = null;
        this.forceZoneChange = false;
        debugLog('Game', ' Zone transition complete');
        // Refresh NPC quest markers for new zone
        this.updateNpcQuestMarkers();
        this.updateQuestObjectiveMarkers();
        // Auto-save after zone change
        this.triggerAutoSave('zone_change');
      }
    }
  }

  private renderZoneTransition(): void {
    if (!this.zoneTransitionActive && this.zoneTransitionFade <= 0) return;

    const ctx = this.renderer.context;
    const w = this.canvas.width;
    const h = this.canvas.height;

    ctx.save();

    const currentZoneId = this.pendingZoneId || (this.world ? this.world.getCurrentZoneId() : '');
    const zoneColor = ZONE_COLORS[currentZoneId] || '0, 0, 0';

    // Main fade overlay with zone-tinted color
    ctx.globalAlpha = this.zoneTransitionFade;
    ctx.fillStyle = `rgb(${zoneColor})`;
    ctx.fillRect(0, 0, w, h);

    // Dark overlay on top for readability
    ctx.globalAlpha = this.zoneTransitionFade * 0.7;
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, w, h);

    // Show zone name, level range and description during peak fade (>50%)
    if (this.zoneTransitionFade > 0.5 && this.world) {
      const textAlpha = (this.zoneTransitionFade - 0.5) * 2;

      // Decorative line above zone name
      ctx.globalAlpha = textAlpha * 0.4;
      const lineW = 120;
      const lineGrad = ctx.createLinearGradient(w / 2 - lineW, h / 2 - 55, w / 2 + lineW, h / 2 - 55);
      lineGrad.addColorStop(0, 'rgba(255, 215, 0, 0)');
      lineGrad.addColorStop(0.3, `rgba(${zoneColor}, 0.8)`);
      lineGrad.addColorStop(0.7, `rgba(${zoneColor}, 0.8)`);
      lineGrad.addColorStop(1, 'rgba(255, 215, 0, 0)');
      ctx.fillStyle = lineGrad;
      ctx.fillRect(w / 2 - lineW, h / 2 - 56, lineW * 2, 2);

      // Zone name - large with subtle glow
      ctx.globalAlpha = textAlpha;
      ctx.save();
      ctx.shadowColor = `rgba(${zoneColor}, 0.6)`;
      ctx.shadowBlur = 15;
      ctx.font = 'bold 36px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        this.world.getZoneName(),
        w / 2,
        h / 2 - 30
      );
      ctx.restore();

      // Zone level range preview
      const zoneConfig = ZONES[currentZoneId as ZoneType];
      if (zoneConfig && zoneConfig.levelRange) {
        ctx.globalAlpha = textAlpha * 0.6;
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = '#aaa';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          `Lv ${zoneConfig.levelRange.min} - ${zoneConfig.levelRange.max}`,
          w / 2,
          h / 2 + 5
        );
      }

      // Zone description - smaller
      const description = ZONE_DESCRIPTIONS[currentZoneId] || '';
      if (description) {
        ctx.globalAlpha = textAlpha * 0.7;
        ctx.font = 'italic 18px Arial';
        ctx.fillStyle = '#cccccc';
        ctx.fillText(
          description,
          w / 2,
          h / 2 + 35
        );
      }

      // Decorative line below description
      ctx.globalAlpha = textAlpha * 0.4;
      ctx.fillStyle = lineGrad;
      ctx.fillRect(w / 2 - lineW, h / 2 + 55, lineW * 2, 2);
    }

    ctx.restore();
  }

  private renderDamageVignette(): void {
    if (this.screenDamageFlash <= 0) return;

    const ctx = this.renderer.context;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const intensity = Math.min(1, this.screenDamageFlash / 0.3);

    // Cache the gradient shape; only recreate when canvas size changes.
    // Color stops use alpha=1; we vary visibility via ctx.globalAlpha instead.
    if (w !== this._dmgVigW || h !== this._dmgVigH || !this._cachedDmgVigGrad) {
      this._cachedDmgVigGrad = ctx.createRadialGradient(
        w / 2, h / 2, Math.min(w, h) * 0.3,
        w / 2, h / 2, Math.max(w, h) * 0.7
      );
      this._cachedDmgVigGrad.addColorStop(0, 'rgba(255, 0, 0, 0)');
      this._cachedDmgVigGrad.addColorStop(1, 'rgba(180, 0, 0, 1)');
      this._dmgVigW = w;
      this._dmgVigH = h;
    }

    ctx.save();
    ctx.globalAlpha = intensity * 0.35;
    ctx.fillStyle = this._cachedDmgVigGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  private renderScreenFlash(): void {
    const flash = getScreenShake().getFlash();
    if (!flash) return;

    const ctx = this.renderer.context;
    ctx.save();
    ctx.globalAlpha = flash.alpha;
    ctx.fillStyle = flash.color;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.restore();
  }

  /** Get or create a cached atmosphere gradient (only recreated on canvas resize) */
  private getAtmoGrad(key: string, createFn: () => CanvasGradient): CanvasGradient {
    let grad = this._cachedAtmoGrads.get(key);
    if (!grad) {
      grad = createFn();
      this._cachedAtmoGrads.set(key, grad);
    }
    return grad;
  }

  private renderZoneAtmosphere(): void {
    if (!this.world) return;

    const ctx = this.renderer.context;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const zoneId = this.world.getCurrentZoneId();
    const time = Date.now() / 1000;

    // Invalidate cached gradients when canvas size changes
    if (w !== this._atmoGradW || h !== this._atmoGradH) {
      this._cachedAtmoGrads.clear();
      this._atmoGradW = w;
      this._atmoGradH = h;
    }

    ctx.save();

    // Animated fog: repaint offscreen canvas every ~200ms instead of every frame
    const fogInterval = 0.2;
    const needsFogRepaint = !this._fogCanvas ||
      this._fogCanvas.width !== w || this._fogCanvas.height !== h ||
      this._fogZone !== zoneId ||
      time - this._fogLastTime > fogInterval;

    if (needsFogRepaint) {
      if (!this._fogCanvas || this._fogCanvas.width !== w || this._fogCanvas.height !== h) {
        this._fogCanvas = document.createElement('canvas');
        this._fogCanvas.width = w;
        this._fogCanvas.height = h;
        this._fogCtx = this._fogCanvas.getContext('2d')!;
      }
      const fc = this._fogCtx!;
      fc.clearRect(0, 0, w, h);
      this._fogZone = zoneId;
      this._fogLastTime = time;

      switch (zoneId) {
        case 'karanlik_orman': {
          fc.globalAlpha = 0.06;
          fc.fillStyle = '#0a2a0a';
          fc.fillRect(0, 0, w, h);
          fc.globalAlpha = 0.04;
          const fogX = Math.sin(time * 0.3) * w * 0.1;
          const fogGrad = fc.createRadialGradient(w * 0.3 + fogX, h * 0.5, w * 0.1, w * 0.3 + fogX, h * 0.5, w * 0.4);
          fogGrad.addColorStop(0, 'rgba(100, 150, 100, 0.15)');
          fogGrad.addColorStop(1, 'rgba(100, 150, 100, 0)');
          fc.fillStyle = fogGrad;
          fc.fillRect(0, 0, w, h);
          const vignette = this.getAtmoGrad('forest_vignette', () => {
            const g = ctx.createRadialGradient(w / 2, h / 2, w * 0.25, w / 2, h / 2, w * 0.7);
            g.addColorStop(0, 'rgba(0, 0, 0, 0)');
            g.addColorStop(1, 'rgba(0, 20, 0, 0.2)');
            return g;
          });
          fc.globalAlpha = 1;
          fc.fillStyle = vignette;
          fc.fillRect(0, 0, w, h);
          break;
        }
        case 'ruh_batakligi': {
          fc.globalAlpha = 0.08;
          fc.fillStyle = '#1a0a2a';
          fc.fillRect(0, 0, w, h);
          const fog1X = Math.sin(time * 0.2) * w * 0.15;
          const fog2X = Math.cos(time * 0.15 + 1) * w * 0.12;
          fc.globalAlpha = 0.06;
          const swampFog1 = fc.createRadialGradient(w * 0.4 + fog1X, h * 0.4, w * 0.05, w * 0.4 + fog1X, h * 0.4, w * 0.35);
          swampFog1.addColorStop(0, 'rgba(80, 120, 80, 0.2)');
          swampFog1.addColorStop(1, 'rgba(80, 120, 80, 0)');
          fc.fillStyle = swampFog1;
          fc.fillRect(0, 0, w, h);
          const swampFog2 = fc.createRadialGradient(w * 0.7 + fog2X, h * 0.6, w * 0.05, w * 0.7 + fog2X, h * 0.6, w * 0.3);
          swampFog2.addColorStop(0, 'rgba(100, 60, 140, 0.15)');
          swampFog2.addColorStop(1, 'rgba(100, 60, 140, 0)');
          fc.fillStyle = swampFog2;
          fc.fillRect(0, 0, w, h);
          const swampVig = this.getAtmoGrad('swamp_vignette', () => {
            const g = ctx.createRadialGradient(w / 2, h / 2, w * 0.2, w / 2, h / 2, w * 0.65);
            g.addColorStop(0, 'rgba(0, 0, 0, 0)');
            g.addColorStop(1, 'rgba(20, 10, 30, 0.25)');
            return g;
          });
          fc.globalAlpha = 1;
          fc.fillStyle = swampVig;
          fc.fillRect(0, 0, w, h);
          break;
        }
        case 'erlik_tapinagi': {
          fc.globalAlpha = 0.05;
          fc.fillStyle = '#1a0505';
          fc.fillRect(0, 0, w, h);
          const pulse = Math.sin(time * 1.5) * 0.02 + 0.03;
          fc.globalAlpha = pulse;
          fc.fillStyle = '#330000';
          fc.fillRect(0, 0, w, h);
          const templeVig = this.getAtmoGrad('temple_vignette', () => {
            const g = ctx.createRadialGradient(w / 2, h / 2, w * 0.15, w / 2, h / 2, w * 0.6);
            g.addColorStop(0, 'rgba(0, 0, 0, 0)');
            g.addColorStop(1, 'rgba(10, 0, 0, 0.35)');
            return g;
          });
          fc.globalAlpha = 1;
          fc.fillStyle = templeVig;
          fc.fillRect(0, 0, w, h);
          break;
        }
        case 'otag_koyu': {
          fc.globalAlpha = 0.03;
          fc.fillStyle = '#2a2010';
          fc.fillRect(0, 0, w, h);
          const villageVig = this.getAtmoGrad('village_vignette', () => {
            const g = ctx.createRadialGradient(w / 2, h / 2, w * 0.3, w / 2, h / 2, w * 0.7);
            g.addColorStop(0, 'rgba(0, 0, 0, 0)');
            g.addColorStop(1, 'rgba(20, 15, 5, 0.1)');
            return g;
          });
          fc.globalAlpha = 1;
          fc.fillStyle = villageVig;
          fc.fillRect(0, 0, w, h);
          break;
        }
      }
    }

    // Single drawImage blit of pre-rendered fog (1 compositing op instead of 3-5)
    if (this._fogCanvas && this._fogZone) {
      ctx.drawImage(this._fogCanvas, 0, 0);
    }

    ctx.restore();
  }

  private setupInventoryEvents(): void {
    // Mouse move for inventory
    this.canvas.addEventListener('mousemove', (e) => {
      if (this.inventoryUI?.isVisible()) {
        const rect = this.getCanvasRect();
        this.inventoryUI.handleMouseMove(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Mouse down for inventory drag
    this.canvas.addEventListener('mousedown', (e) => {
      if (this.inventoryUI?.isVisible() && e.button === 0) {
        const rect = this.getCanvasRect();
        this.inventoryUI.handleMouseDown(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Mouse up for inventory
    this.canvas.addEventListener('mouseup', (e) => {
      if (this.inventoryUI?.isVisible()) {
        const rect = this.getCanvasRect();
        this.inventoryUI.handleMouseUp(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Right click for quick equip
    this.canvas.addEventListener('contextmenu', (e) => {
      if (this.inventoryUI?.isVisible()) {
        e.preventDefault();
        const rect = this.getCanvasRect();
        this.inventoryUI.handleRightClick(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });
  }

  private setupCraftingEvents(): void {
    // Mouse move for crafting UI
    this.canvas.addEventListener('mousemove', (e) => {
      if (this.craftingUI?.isVisible()) {
        const rect = this.getCanvasRect();
        this.craftingUI.handleMouseMove(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Mouse click for crafting UI
    this.canvas.addEventListener('click', (e) => {
      if (this.craftingUI?.isVisible()) {
        const rect = this.getCanvasRect();
        this.craftingUI.handleClick(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });
  }

  private setupSkillEditorEvents(): void {
    // Mouse move for skill editor (for slider dragging)
    this.canvas.addEventListener('mousemove', (e) => {
      if (this.skillEditorUI?.isVisible()) {
        const rect = this.getCanvasRect();
        this.skillEditorUI.handleMouseMove(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Mouse down for skill editor (for clicks and slider start)
    this.canvas.addEventListener('mousedown', (e) => {
      if (this.skillEditorUI?.isVisible() && e.button === 0) {
        const rect = this.getCanvasRect();
        this.skillEditorUI.handleMouseDown(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Mouse up for skill editor (for slider end)
    this.canvas.addEventListener('mouseup', (e) => {
      if (this.skillEditorUI?.isVisible()) {
        const rect = this.getCanvasRect();
        this.skillEditorUI.handleMouseUp(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Wheel for skill list scrolling
    this.canvas.addEventListener('wheel', (e) => {
      if (this.skillEditorUI?.isVisible()) {
        e.preventDefault();
        this.skillEditorUI.handleWheel(e.deltaY);
      }
    });
  }

  private setupPassiveTreeEvents(): void {
    // Mouse move for passive tree
    this.canvas.addEventListener('mousemove', (e) => {
      if (this.passiveTreeUI?.isVisible()) {
        const rect = this.getCanvasRect();
        this.passiveTreeUI.handleMouseMove(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Mouse down for passive tree drag
    this.canvas.addEventListener('mousedown', (e) => {
      if (this.passiveTreeUI?.isVisible()) {
        const rect = this.getCanvasRect();
        this.passiveTreeUI.handleMouseDown(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Mouse up for passive tree
    this.canvas.addEventListener('mouseup', (e) => {
      if (this.passiveTreeUI?.isVisible()) {
        const rect = this.getCanvasRect();
        this.passiveTreeUI.handleMouseUp(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Wheel for zoom
    this.canvas.addEventListener('wheel', (e) => {
      if (this.passiveTreeUI?.isVisible()) {
        e.preventDefault();
        this.passiveTreeUI.handleWheel(e.deltaY);
      }
    });
  }

  private setupGameMenuEvents(): void {
    // Wire save/load callbacks to HTML game menu
    if (this.gameMenuHTML) {
      this.gameMenuHTML.setSaveCallback((slot) => {
        this.saveGame(slot);
      });
      this.gameMenuHTML.setLoadCallback((slot) => {
        this.loadGame(slot);
      });
    }

    // Mouse move for game menu
    this.canvas.addEventListener('mousemove', (e) => {
      if (this.gameMenu?.isVisible()) {
        const rect = this.getCanvasRect();
        this.gameMenu.handleMouseMove(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Mouse down for game menu (left click)
    this.canvas.addEventListener('mousedown', (e) => {
      if (this.gameMenu?.isVisible() && e.button === 0) {
        const rect = this.getCanvasRect();
        this.gameMenu.handleMouseDown(
          e.clientX - rect.left,
          e.clientY - rect.top,
          'left',
          e.shiftKey
        );
      }
    });

    // Right click for game menu (quick equip in inventory tab)
    this.canvas.addEventListener('contextmenu', (e) => {
      if (this.gameMenu?.isVisible()) {
        e.preventDefault();
        const rect = this.getCanvasRect();
        this.gameMenu.handleMouseDown(
          e.clientX - rect.left,
          e.clientY - rect.top,
          'right',
          e.shiftKey
        );
      }
    });

    // Mouse up for game menu
    this.canvas.addEventListener('mouseup', (e) => {
      if (this.gameMenu?.isVisible()) {
        const rect = this.getCanvasRect();
        this.gameMenu.handleMouseUp(
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      }
    });

    // Wheel for game menu scrolling
    this.canvas.addEventListener('wheel', (e) => {
      if (this.gameMenu?.isVisible()) {
        e.preventDefault();
        const rect = this.getCanvasRect();
        this.gameMenu.handleWheel(
          e.clientX - rect.left,
          e.clientY - rect.top,
          e.deltaY
        );
      }
    });

    // Map tab: set current zone and travel callback
    const mapTab = this.gameMenu?.getMapTab();
    if (mapTab) {
      if (this.world) {
        mapTab.setCurrentRegion(this.world.getCurrentZone().id);
      }
      mapTab.onTravel = (regionId: string) => {
        this.teleportToZone(regionId as ZoneType);
        this.gameMenu?.close();
      };
    }
  }

  private setupWaypointEvents(): void {
    if (!this.waypointPanel) return;

    // Set teleport callback
    this.waypointPanel.onTeleport = (destination: ZoneType) => {
      this.teleportToZone(destination);
    };

    // World map teleport callback
    if (this.worldMapUI) {
      this.worldMapUI.onTeleport = (destination: ZoneType) => {
        this.teleportToZone(destination);
      };
    }

    // Mouse events for waypoint panel
    this.canvas.addEventListener('mousemove', (e) => {
      if (this.waypointPanel?.isOpen()) {
        const rect = this.getCanvasRect();
        this.waypointPanel.handleMouseEvent({
          type: 'move',
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          button: 'left',
        });
      }
    });

    this.canvas.addEventListener('mousedown', (e) => {
      if (this.waypointPanel?.isOpen() && e.button === 0) {
        const rect = this.getCanvasRect();
        this.waypointPanel.handleMouseEvent({
          type: 'down',
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          button: 'left',
        });
      }
    });

    this.canvas.addEventListener('wheel', (e) => {
      if (this.waypointPanel?.isOpen()) {
        e.preventDefault();
        const rect = this.getCanvasRect();
        this.waypointPanel.handleMouseEvent({
          type: 'wheel',
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          button: 'left',
          deltaY: e.deltaY,
        });
      }
    });

    // Setup waypoint manager teleport callback
    const waypointManager = getWaypointManager();
    waypointManager.setTeleportCallback((destination: ZoneType) => {
      this.teleportToZone(destination);
    });

    // Click handler for portals (using mousedown with capture to run first)
    this.canvas.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return; // Left click only
      if (this.state !== 'playing' || !this.world || !this.townPortal) return;
      if (this.vendorUI?.isOpen()) return;
      if (this.gameMenu?.isVisible()) return;
      if (this.zoneTransitionActive) return;

      const rect = this.getCanvasRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      // Convert screen coordinates to world coordinates
      const worldPos = this.world.camera.screenToWorld(screenX, screenY);

      // Portal click detection radius (matches visual glow effect)
      const PORTAL_CLICK_RADIUS = 120;

      // Check if clicking on town portal (priority over everything)
      if (this.townPortal.isTownPortalActive()) {
        const portalPos = this.townPortal.getTownPortalPosition();
        const dx = worldPos.x - portalPos.x;
        const dy = worldPos.y - portalPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        debugLog('Portal', '[Click] Town portal dist:', dist.toFixed(0));

        if (dist < PORTAL_CLICK_RADIUS) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          debugLog('Portal', '[Click] Using town portal!');
          this.useTownPortal();
          return;
        }
      }

      // Check if clicking on return portal
      if (this.townPortal.isReturnPortalActive()) {
        const portalPos = this.townPortal.getReturnPortalPosition();
        const dx = worldPos.x - portalPos.x;
        const dy = worldPos.y - portalPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < PORTAL_CLICK_RADIUS) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.useReturnPortal();
          return;
        }
      }

      // Check if clicking on NPC
      if (!this.dialogUI?.isVisible()) {
        for (const npc of this.world.npcs) {
          const dx = worldPos.x - npc.position.x;
          const dy = worldPos.y - npc.position.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 60) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Quest: talk_to_npc progress
            if (this.questManager) {
              this.questManager.updateProgress('talk_to_npc', npc.id, 1);
            }

            // Build quest dialog if there are quest interactions available
            if (this.questManager && this.dialogUI) {
              const availableQuests = this.questManager.getAvailableQuests()
                .filter(q => q.giverNpcId === npc.id);
              const completableQuests = this.questManager.getActiveQuests()
                .filter(q => {
                  const allDone = q.objectives.every(o => o.completed);
                  return allDone && (!q.turnInNpcId || q.turnInNpcId === npc.id) &&
                    q.giverNpcId === npc.id;
                });
              const activeQuests = this.questManager.getActiveQuests()
                .filter(q => q.giverNpcId === npc.id && !q.objectives.every(o => o.completed));

              const questDialog = npc.buildQuestDialog(availableQuests, completableQuests, activeQuests);

              if (questDialog) {
                this.dialogUI.openWithDialog(npc, questDialog);
              } else {
                this.dialogUI.open(npc);
              }
            } else {
              this.dialogUI?.open(npc);
            }

            return;
          }
        }
      }
    }, true); // Use capture phase to run first
  }

  private teleportToZone(zoneId: ZoneType, force: boolean = true): void {
    if (!this.world) return;

    // Demo mode: block teleport to Act 2+ zones
    if (DEMO_MODE && DEMO_BLOCKED_ZONES.has(zoneId)) {
      getDamageNumbers().spawn(
        this.world.player.position.x,
        this.world.player.position.y - 40,
        t('demo_zone_blocked'),
        'miss'
      );
      this.waypointPanel?.close();
      return;
    }

    // Close waypoint panel if open
    this.waypointPanel?.close();

    // Update map tab current zone
    this.gameMenu?.getMapTab()?.setCurrentRegion(zoneId);

    // Close portals if active
    this.townPortal?.closeTownPortal();
    this.townPortal?.closeReturnPortal();

    // Start zone transition (force=true bypasses adjacency check for map/waypoint teleport)
    this.startZoneTransition(zoneId, force);

    // Update waypoint manager
    const waypointManager = getWaypointManager();
    waypointManager.enterZone(zoneId);
  }

  // Use town portal - teleport to town and spawn return portal
  private useTownPortal(): void {
    if (!this.world || !this.townPortal) return;

    const waypointManager = getWaypointManager();

    // Get return destination BEFORE using the portal
    const returnDest = waypointManager.getReturnPortalDestination();
    const destination = waypointManager.useTownPortal();

    if (destination) {
      // Close the town portal visual
      this.townPortal.closeTownPortal();

      // Store the return destination for spawning return portal after zone change
      this.pendingReturnPortal = returnDest;

      // Store current zone state before leaving (for return portal)
      this.storeCurrentZoneState();

      // Start zone transition
      this.startZoneTransition(destination);
      debugLog('Portal', ' Using town portal to:', destination, '- will return to:', returnDest);
    }
  }

  // Use return portal - teleport back to original zone
  private useReturnPortal(): void {
    if (!this.world || !this.townPortal) return;

    const waypointManager = getWaypointManager();
    const destination = waypointManager.useReturnPortal();

    if (destination) {
      debugLog('Portal',` Using return portal to: ${destination}`);

      // Close the return portal visual
      this.townPortal.closeReturnPortal();

      // Mark that we're returning via portal (to restore state)
      this.isReturningViaPortal = true;

      // Start zone transition
      this.startZoneTransition(destination);
    }
  }

  // Store current zone state before using town portal
  private storeCurrentZoneState(): void {
    if (!this.world) return;

    const currentZone = this.world.getCurrentZoneId() as ZoneType;

    this.storedZoneState = {
      zoneId: currentZone,
      playerPosition: {
        x: this.world.player.position.x,
        y: this.world.player.position.y,
      },
      enemies: this.world.enemies.map(enemy => ({
        type: enemy.constructor.name,
        x: enemy.position.x,
        y: enemy.position.y,
        hp: enemy.stats.hp,
        isAlive: enemy.isAlive,
      })),
      droppedItems: this.world.droppedItems.map(item => ({
        x: item.position.x,
        y: item.position.y,
        item: item.item,
      })),
    };

    debugLog('Portal', ' Zone state stored:', currentZone,
      'enemies:', this.storedZoneState.enemies.length,
      'items:', this.storedZoneState.droppedItems.length);
  }

  // Clear stored zone state
  private clearStoredZoneState(): void {
    this.storedZoneState = null;
  }

  pause(): void {
    if (this.isRunning && this.state === 'playing') {
      this.state = 'paused';
      debugLog('Game', ' Paused');
    }
  }

  resume(): void {
    if (this.state === 'paused') {
      this.state = 'playing';
      this.pauseSettingsOpen = false;
      debugLog('Game', ' Resumed');
    }
  }

  private update(deltaTime: number): void {
    // Update loading/death animation timers
    if (this.state === 'loading') {
      this.loadingAnimTime += deltaTime;
      // Rotate loading tips every 4 seconds with fade
      this.loadingTipTimer += deltaTime;
      if (this.loadingTipTimer > 4) {
        this.loadingTipTimer = 0;
        this.loadingTipIndex = (this.loadingTipIndex + 1) % 12;
        this.loadingTipFade = 0;
      }
      // Fade in new tip
      if (this.loadingTipFade < 1) {
        this.loadingTipFade = Math.min(1, this.loadingTipFade + deltaTime * 2.5);
      }
    }
    if (this.state === 'dead') {
      this.deathFadeIn = Math.min(1, this.deathFadeIn + deltaTime * 1.5);
      // Tick respawn countdown
      if (this.deathCountdown > 0) {
        this.deathCountdown -= deltaTime;
        if (this.deathCountdown <= 0) {
          this.deathCountdown = 0;
          this.deathCountdownReady = true;
        }
      }
    } else {
      this.deathFadeIn = 0;
    }

    // Update main menu when in menu state
    if (this.state === 'menu') {
      this.mainMenuUI.update(deltaTime);
      this.inputManager.update();
      return;
    }

    // Update zone transition (always)
    this.updateZoneTransition(deltaTime);

    // Don't update game during zone transition
    if (this.zoneTransitionActive) {
      this.inputManager.update();
      return;
    }

    // Block game input when vendor or dialog is open (HTML overlays handle their own events)
    if (this.vendorUI?.isOpen()) {
      this.vendorUI.update(deltaTime);
      this.inputManager.update();
      return;
    }
    if (this.dialogUI?.isVisible()) {
      this.inputManager.update();
      return;
    }

    // Update quest UI if visible
    if (this.questUI?.isVisible()) {
      this.questUI.update(deltaTime);
      this.inputManager.update();
      return;
    }

    // Update world map UI if visible
    if (this.worldMapUI?.isVisible()) {
      this.worldMapUI.update(deltaTime, this.inputManager);
      this.inputManager.update();
      return;
    }

    // Update game menu if visible
    if (this.gameMenu?.isVisible()) {
      this.gameMenu.update(deltaTime);
      this.inputManager.update();
      return;
    }

    // Update waypoint panel if visible
    if (this.waypointPanel?.isOpen()) {
      this.waypointPanel.update(deltaTime);
      this.inputManager.update();
      return;
    }

    // Don't update game if UI menus are open
    if (this.passiveTreeUI?.isVisible() || this.saveMenuUI.isVisible() || this.inventoryUI?.isVisible() || this.craftingUI?.isVisible() || this.skillEditorUI?.isVisible()) {
      // Update crafting UI animation
      if (this.craftingUI?.isVisible()) {
        this.craftingUI.update(deltaTime);
      }
      // Update skill editor UI
      if (this.skillEditorUI?.isVisible()) {
        this.skillEditorUI.update(deltaTime);
        this.skillEditorUI.handleInput(this.inputManager);
      }
      this.inputManager.update();
      return;
    }

    if (this.state === 'playing' && this.world) {
      // Track play time
      this.playTime += deltaTime;

      // Demo complete overlay - update fade and handle input
      if (this.demoComplete) {
        this.demoFadeIn = Math.min(1, this.demoFadeIn + deltaTime * 2);
        this.inputManager.update();
        return;
      }

      // Hitstop: skip game update during freeze frame for impact feel
      if (getScreenShake().isHitStopped()) {
        this.inputManager.update();
        return;
      }

      // Handle player input (movement, dodge)
      this.world.player.handleInput(this.inputManager, deltaTime);

      // Update HTML HUD
      if (this.hudHTML?.isShown()) {
        // Feed enemy positions to minimap
        this.hudHTML.setEnemies(
          this.world.enemies.map(e => ({ x: e.position.x, y: e.position.y, isAlive: e.isAlive }))
        );
        // Feed NPC positions to minimap
        this.hudHTML.setNPCs(
          this.world.npcs.map(n => ({ x: n.position.x, y: n.position.y }))
        );
        // Feed zone exits to minimap
        const exits = this.world.getZoneExits();
        this.hudHTML.setExits(
          exits.map(e => ({ x: e.position.x, y: e.position.y, label: e.targetZone || '' }))
        );
        // Feed world dimensions for minimap scaling (isometric bounds)
        const map = this.world.map;
        const tw = map.getTileWidth();
        const th = map.getTileHeight();
        const mw = map.getWidth();
        const mh = map.getHeight();
        // Isometric world bounds: X from -(H*tw/2) to (W*tw/2), Y from 0 to (W+H)*th/2
        const worldW = (mw + mh) * (tw / 2);
        const worldH = (mw + mh) * (th / 2);
        // Offset so 0,0 maps to the left corner
        const offsetX = mh * (tw / 2);
        this.hudHTML.setWorldDimensions(worldW, worldH, offsetX);
        // Feed gold
        this.hudHTML.setGold(this.world.player.getGold());
        // Feed boss info
        this.hudHTML.setBossInfo(this.world.getBossInfo());

        this.hudHTML.update(this.world.player, deltaTime);
      }

      // Get target position - mouse for PC, auto-target for controller/mobile
      const targetPos = this.getSkillTargetPosition();

      // Handle skill attacks (1-6 keys) - keyboard uses auto-target
      for (let i = 0; i < 6; i++) {
        const keyCode = SKILL_DIGIT_KEYS[i];
        const skill = this.world.player.skillHotbar.get(i);

        // Check if this is a beam skill that needs special handling
        if (skill && isSkillV2(skill) && (skill as CustomSkillV2).deliveryType === 'beam') {
          const beamSkill = skill as CustomSkillV2;

          // Check if mouse is controlling this slot's beam (slot 0 = left click, slot 1 = right click)
          const mouseHoldingBeam = (i === 0 && this.inputManager.isMouseButtonDown('left')) ||
                                   (i === 1 && this.inputManager.isMouseButtonDown('right'));

          if (this.inputManager.isKeyPressed(keyCode)) {
            // Start beam on key press
            if (this.world.player.canUseSkillV2(beamSkill)) {
              this.world.player.startChanneling(beamSkill);
              this.world.handleSkillV2(beamSkill, targetPos);
            }
          } else if (this.inputManager.isKeyDown(keyCode)) {
            // Continue beam while held - update direction
            if (beamSkill.isActive) {
              const dx = targetPos.x - this.world.player.position.x;
              const dy = targetPos.y - this.world.player.position.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              const direction = dist > 0 ? { x: dx / dist, y: dy / dist } : { x: 1, y: 0 };
              this.world.updateBeam(beamSkill, direction, dist);
            }
          } else if (!mouseHoldingBeam) {
            // Stop beam when key released (but only if mouse isn't holding it)
            if (beamSkill.isActive) {
              this.world.stopBeam(beamSkill.id, this.getSkillTargetPosition());
            }
          }
        } else {
          // Normal skill handling (non-beam)
          if (this.inputManager.isKeyPressed(keyCode)) {
            const result = this.world.player.useSkill(i);
            if (result.success && result.skill) {
              const autoTarget = this.getAutoTargetPosition(this.getSkillRange(result.skill));
              this.handleSkillResult(result.skill, result.damage, autoTarget, result.isV2);
            }
          }
        }
      }

      // Handle left click (skill 0 or basic attack) - uses mouse position
      const skill0 = this.world.player.skillHotbar.get(0);
      // Skip mouse beam handling if keyboard is controlling this slot's beam
      const keyHoldingSlot0 = this.inputManager.isKeyDown(SKILL_DIGIT_KEYS[0]);
      if (skill0 && isSkillV2(skill0) && (skill0 as CustomSkillV2).deliveryType === 'beam' && !keyHoldingSlot0) {
        // Beam skill on slot 0 - handle with mouse (only if keyboard isn't holding it)
        const beamSkill = skill0 as CustomSkillV2;
        if (this.inputManager.isMouseButtonPressed('left')) {
          if (this.world.player.canUseSkillV2(beamSkill)) {
            this.world.player.startChanneling(beamSkill);
            this.world.handleSkillV2(beamSkill, targetPos);
          }
        } else if (this.inputManager.isMouseButtonDown('left')) {
          if (beamSkill.isActive) {
            const dx = targetPos.x - this.world.player.position.x;
            const dy = targetPos.y - this.world.player.position.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const direction = dist > 0 ? { x: dx / dist, y: dy / dist } : { x: 1, y: 0 };
            this.world.updateBeam(beamSkill, direction);
          }
        } else {
          if (beamSkill.isActive) {
            this.world.stopBeam(beamSkill.id);
          }
        }
      } else if (this.inputManager.isMouseButtonPressed('left') && !keyHoldingSlot0) {
        if (skill0 && this.canUseSkill(skill0, this.world.player.stats.mana)) {
          const result = this.world.player.useSkill(0);
          if (result.success && result.skill) {
            this.handleSkillResult(result.skill, result.damage, targetPos, result.isV2);
          }
        } else {
          this.world.handlePlayerAttack();
        }
      }

      // Handle right click (skill 1) - uses mouse position
      const skill1 = this.world.player.skillHotbar.get(1);
      // Skip mouse beam handling if keyboard is controlling this slot's beam
      const keyHoldingSlot1 = this.inputManager.isKeyDown(SKILL_DIGIT_KEYS[1]);
      if (skill1 && isSkillV2(skill1) && (skill1 as CustomSkillV2).deliveryType === 'beam' && !keyHoldingSlot1) {
        // Beam skill on slot 1 - handle with mouse (only if keyboard isn't holding it)
        const beamSkill = skill1 as CustomSkillV2;
        if (this.inputManager.isMouseButtonPressed('right')) {
          if (this.world.player.canUseSkillV2(beamSkill)) {
            this.world.player.startChanneling(beamSkill);
            this.world.handleSkillV2(beamSkill, targetPos);
          }
        } else if (this.inputManager.isMouseButtonDown('right')) {
          if (beamSkill.isActive) {
            const dx = targetPos.x - this.world.player.position.x;
            const dy = targetPos.y - this.world.player.position.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const direction = dist > 0 ? { x: dx / dist, y: dy / dist } : { x: 1, y: 0 };
            this.world.updateBeam(beamSkill, direction);
          }
        } else {
          if (beamSkill.isActive) {
            this.world.stopBeam(beamSkill.id);
          }
        }
      } else if (this.inputManager.isMouseButtonPressed('right') && !keyHoldingSlot1) {
        const result = this.world.player.useSkill(1);
        if (result.success && result.skill) {
          this.handleSkillResult(result.skill, result.damage, targetPos, result.isV2);
        }
      }

      // Handle touch input (mobile) - uses auto-target
      if (this.inputManager.isTouchActive()) {
        // Touch can trigger skill 0
        const skill = this.world.player.skillHotbar.get(0);
        if (skill && this.canUseSkill(skill, this.world.player.stats.mana)) {
          const result = this.world.player.useSkill(0);
          if (result.success && result.skill) {
            const autoTarget = this.getAutoTargetPosition(this.getSkillRange(result.skill));
            this.handleSkillResult(result.skill, result.damage, autoTarget, result.isV2);
          }
        }
      }

      // Resolve pending skill when cast/attack animation finishes
      if (this.pendingAttack && this.wasAttacking && !this.world.player.isAttacking) {
        const pa = this.pendingAttack;
        this.pendingAttack = null;
        // Route to correct handler based on skill type
        if (pa.isV2 && isSkillV2(pa.skill)) {
          const v2 = pa.skill as CustomSkillV2;
          const specialTypes = ['trap', 'totem', 'buff', 'curse'];
          if (specialTypes.includes(v2.deliveryType)) {
            this.world.handleSkillV2(v2, pa.targetPos);
          } else {
            this.world.handleSkillAttack(pa.skill, pa.damage, pa.targetPos, pa.isV2);
          }
        } else {
          this.world.handleSkillAttack(pa.skill, pa.damage, pa.targetPos, pa.isV2);
        }
      }
      this.wasAttacking = this.world.player.isAttacking;

      // Update world
      this.world.update(deltaTime);

      // Track player damage for screen flash
      if (this.world) {
        const currentHp = this.world.player.stats.hp;
        const maxHp = this.world.player.stats.maxHp;
        if (currentHp < this.lastPlayerHp) {
          const damagePercent = (this.lastPlayerHp - currentHp) / maxHp;
          if (damagePercent >= 0.20) {
            this.screenDamageFlash = 0.3; // Heavy hit flash duration
          }
        }
        this.lastPlayerHp = currentHp;
      }

      // Decay screen damage flash
      if (this.screenDamageFlash > 0) {
        this.screenDamageFlash -= deltaTime;
      }

      // Update NPC quest markers (throttled to every 500ms)
      this._questMarkerTimer += deltaTime;
      if (this._questMarkerTimer >= 0.5) {
        this._questMarkerTimer = 0;
        this.updateNpcQuestMarkers();
        this.updateQuestObjectiveMarkers();
      }

      // Update town portal
      if (this.townPortal) {
        this.townPortal.update(deltaTime);
      }

      // Update potion system
      if (this.potionSystem) {
        this.potionSystem.update(deltaTime);

        // TODO: potion state in HudHTML
      }

      // Check for player death
      if (!this.world.player.isAlive) {
        this.state = 'dead';

        // Capture cause of death from last damage source
        const sourceName = this.world.player.lastDamageSourceName;
        if (sourceName) {
          this.deathCauseText = t('game.death.slainBy', { enemy: sourceName });
        } else {
          this.deathCauseText = t('game.death.slainByUnknown');
        }

        // Apply 5% gold penalty on death
        const player = this.world.player;
        const currentGold = player.inventory.getGold();
        this.deathGoldLost = Math.floor(currentGold * 0.05);
        if (this.deathGoldLost > 0) {
          player.inventory.removeGold(this.deathGoldLost);
        }

        // Reset death countdown timer
        this.deathCountdown = 5;
        this.deathCountdownReady = false;

        // Zoom out on death for dramatic effect
        this.world.camera.playerDeathZoom();

        // Death sound and heavy screen shake
        this.audioManager.play('enemy_death');
        getScreenShake().shake({ intensity: 15, duration: 0.8 });
      }

      // Auto-save system
      this.autoSaveTimer += deltaTime;
      if (this.autoSaveTimer >= this.autoSaveInterval) {
        this.autoSaveTimer = 0;
        this.triggerAutoSave('timer');
      }

      // Level up detection - update quests and auto-save
      if (this.world.player.level > this.lastPlayerLevel) {
        this.lastPlayerLevel = this.world.player.level;
        if (this.questManager) {
          this.questManager.updateProgress('reach_level', this.world.player.level.toString(), 1);
          this.questManager.updateQuestAvailability(this.world.player.level);
        }
        this.triggerAutoSave('level_up');
      }

      // Update auto-save indicator timer
      if (this.autoSaveIndicatorTimer > 0) {
        this.autoSaveIndicatorTimer -= deltaTime;
        if (this.autoSaveIndicatorTimer <= 0) {
          this.isAutoSaving = false;
        }
      }
    }

    // ── Tutorial System update & triggers ─────────────────────────────
    if (this.state === 'playing' && this.world) {
      this.updateTutorialTriggers();
      this.tutorialSystem.update(deltaTime);
    }

    // Update input state AFTER processing (for next frame comparison)
    this.inputManager.update();
  }

  /**
   * Check tutorial trigger conditions and fire hints when appropriate.
   */
  private updateTutorialTriggers(): void {
    if (!this.world) return;
    // Skip all checks once every tutorial has been shown
    if (this.tutorialSystem.allComplete()) return;
    const player = this.world.player;

    // "attack" - when first enemy is nearby (within 250 units)
    if (!this.tutorialSystem.hasBeenShown('attack') && this.world.enemies.length > 0) {
      for (const enemy of this.world.enemies) {
        if (!enemy.isAlive) continue;
        const dx = enemy.position.x - player.position.x;
        const dy = enemy.position.y - player.position.y;
        if (dx * dx + dy * dy < 250 * 250) {
          this.tutorialSystem.triggerHint('attack');
          break;
        }
      }
    }

    // "dodge" - when player takes first damage (HP dropped from lastPlayerHp)
    if (!this.tutorialSystem.hasBeenShown('dodge')) {
      const currentHp = player.stats.hp;
      if (currentHp < player.stats.maxHp && currentHp < this.lastPlayerHp) {
        this.tutorialSystem.triggerHint('dodge');
      }
    }

    // "pickup" - when there are dropped items on the ground
    if (!this.tutorialSystem.hasBeenShown('pickup') && this.world.droppedItems.length > 0) {
      this.tutorialSystem.triggerHint('pickup');
    }

    // "inventory" - when player has picked up at least one item
    // (inventory has items and pickup hint was already shown)
    if (!this.tutorialSystem.hasBeenShown('inventory')
        && this.tutorialSystem.hasBeenShown('pickup')
        && player.inventory.getAllItemsAllTabs().length > 0) {
      this.tutorialSystem.triggerHint('inventory');
    }

    // "skills" - at level 2
    if (!this.tutorialSystem.hasBeenShown('skills') && player.level >= 2) {
      this.tutorialSystem.triggerHint('skills');
    }

    // "potion" - when HP drops below 50%
    if (!this.tutorialSystem.hasBeenShown('potion')) {
      const hpPercent = player.stats.hp / player.stats.maxHp;
      if (hpPercent < 0.5 && player.isAlive) {
        this.tutorialSystem.triggerHint('potion');
      }
    }
  }

  /**
   * Check if a skill can be used (works with both V1 and V2 skills)
   */
  private canUseSkill(skill: HotbarSkill, currentMana: number): boolean {
    // Global AOE cooldown check - reject AoE skills while on cooldown
    if (isSkillV2(skill)) {
      return SkillCreatorV2.canUse(skill, currentMana, this.world?.player.stats.maxMana || 100);
    } else {
      return SkillCreator.canUse(skill, currentMana);
    }
  }

  /**
   * Get effective range of a skill (works with both V1 and V2 skills)
   */
  private getSkillRange(skill: HotbarSkill): number {
    if (isSkillV2(skill)) {
      return SkillCreatorV2.getEffectiveRange(skill);
    } else {
      return SkillCreator.getEffectiveRange(skill);
    }
  }

  /**
   * Handle skill result - routes to appropriate handler based on skill type
   */
  private handleSkillResult(skill: HotbarSkill, damage: number, targetPos: Vector2, isV2: boolean): void {
    if (!this.world) return;

    if (isV2 && isSkillV2(skill)) {
      const v2 = skill as CustomSkillV2;

      // Instant skills: aura (toggle), beam (channel), minion (toggle) - no cast time
      if (v2.deliveryType === 'aura' || v2.deliveryType === 'beam' || v2.deliveryType === 'minion') {
        this.world.handleSkillV2(v2, targetPos);
        return;
      }

      // Melee thrust: dashes immediately (no defer)
      if (v2.deliveryType === 'melee' && v2.meleeType === 'thrust') {
        this.world.handleSkillAttack(skill, damage, targetPos, isV2);
        return;
      }

      // All other skills: defer until cast/attack animation finishes
      // melee strike, projectile, aoe, curse, trap, totem, buff
      this.pendingAttack = { skill, damage, targetPos, isV2 };
      return;
    }

    // V1 skills: apply immediately
    this.world.handleSkillAttack(skill, damage, targetPos, isV2);
  }

  /**
   * Get skill target position from mouse
   */
  private getSkillTargetPosition(): Vector2 {
    if (!this.world) return { x: 0, y: 0 };

    const mouseScreen = this.inputManager.getMousePosition();
    return this.world.camera.screenToWorld(mouseScreen.x, mouseScreen.y);
  }

  /**
   * Get auto-target position for controller/mobile/keyboard
   * Finds best enemy in range and returns their position
   */
  private getAutoTargetPosition(range: number): Vector2 {
    if (!this.world) return { x: 0, y: 0 };

    const player = this.world.player;

    // Find best target in direction player is facing
    const bestTarget = TargetingSystem.findBestTarget(
      player,
      this.world.enemies,
      range * 1.5 // Slightly larger search range
    );

    if (bestTarget) {
      // Aim at the enemy
      return TargetingSystem.getAimPosition(bestTarget);
    }

    // No target found - aim in front of player
    const facingVec = player.getFacingVector();
    return {
      x: player.position.x + facingVec.x * range,
      y: player.position.y + facingVec.y * range,
    };
  }

  private render(): void {
    this.renderer.clear();

    switch (this.state) {
      case 'loading':
        this.renderLoading();
        break;
      case 'menu':
        this.renderMenu();
        break;
      case 'playing':
        this.renderGame();
        // Render inventory UI on top if open
        if (this.inventoryUI?.isVisible()) {
          this.inventoryUI.render(this.renderer);
        }
        // Render passive tree UI on top if open
        if (this.passiveTreeUI?.isVisible()) {
          this.passiveTreeUI.render(this.renderer);
        }
        // Render skill editor UI on top if open
        if (this.skillEditorUI?.isVisible()) {
          this.skillEditorUI.render(this.renderer);
        }
        // Render save menu UI on top if open
        if (this.saveMenuUI.isVisible()) {
          this.saveMenuUI.render(this.renderer);
        }
        // Render crafting UI on top if open
        if (this.craftingUI?.isVisible()) {
          this.craftingUI.render(this.renderer);
        }
        // Render quest UI on top if open
        if (this.questUI?.isVisible()) {
          this.questUI.render(this.renderer);
        }
        // Tracked quest is now shown in HTML HUD (HudHTML quest tracker)
        // DialogHTML and VendorHTML render via DOM overlay (no canvas rendering needed)
        // Render world map UI on top
        if (this.worldMapUI?.isVisible()) {
          this.worldMapUI.render(this.renderer);
        }
        // Render game menu on top
        if (this.gameMenu?.isVisible()) {
          this.gameMenu.render(this.renderer);
        }
        // Render waypoint panel on top
        if (this.waypointPanel?.isOpen()) {
          this.waypointPanel.render(this.renderer.context);
        }
        // Render tutorial hint overlay
        this.tutorialSystem.render(this.renderer);
        // Render zone atmosphere overlay (fog, tint)
        this.renderZoneAtmosphere();
        // Render zone transition overlay
        this.renderZoneTransition();
        // Render screen damage vignette
        this.renderDamageVignette();
        // Render screen flash overlay (hit feedback)
        this.renderScreenFlash();
        // Demo complete overlay (on top of everything)
        if (this.demoComplete) {
          this.renderDemoCompleteScreen();
        }
        break;
      case 'paused':
        this.renderGame();
        this.renderPauseOverlay();
        break;
      case 'dead':
        this.renderGame();
        this.renderDeathOverlay();
        break;
    }

    // Always show FPS
    this.renderDebugInfo();

    // Auto-save indicator
    this.renderAutoSaveIndicator();

  }

  private renderLoading(): void {
    const ctx = this.renderer.context;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const cx = w / 2;
    const cy = h / 2;
    const time = this.loadingAnimTime;

    // Animated gradient background
    const bgGrad = ctx.createRadialGradient(
      cx + Math.sin(time * 0.3) * 100, cy + Math.cos(time * 0.4) * 50, 0,
      cx, cy, Math.max(w, h) * 0.7
    );
    bgGrad.addColorStop(0, '#12101a');
    bgGrad.addColorStop(0.5, '#0a0a14');
    bgGrad.addColorStop(1, '#050510');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, w, h);

    // Ambient floating particles
    ctx.save();
    for (let i = 0; i < 20; i++) {
      const px = (cx + Math.sin(time * 0.2 + i * 1.7) * (w * 0.4)) % w;
      const py = (cy + Math.cos(time * 0.15 + i * 2.3) * (h * 0.3)) % h;
      const size = 1 + Math.sin(time + i) * 0.5;
      const alpha = 0.15 + Math.sin(time * 0.5 + i) * 0.1;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#ffd700';
      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // Game title with pulsing glow
    ctx.save();
    const glowIntensity = 15 + Math.sin(time * 2) * 8;
    ctx.font = 'bold 48px serif';
    ctx.fillStyle = '#ffd700';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#ff8800';
    ctx.shadowBlur = glowIntensity;
    ctx.fillText(t('mainMenu.gameTitle'), cx, cy - 90);
    ctx.shadowBlur = 0;
    ctx.font = '18px sans-serif';
    ctx.fillStyle = '#887744';
    ctx.fillText(t('mainMenu.gameSubtitle'), cx, cy - 55);
    ctx.restore();

    // Spinning loader with glow
    ctx.save();
    ctx.translate(cx, cy + 5);
    ctx.lineCap = 'round';
    const segments = 10;
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2 + time * 3;
      const alpha = (i / segments);
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = '#ffd700';
      ctx.lineWidth = 2 + alpha * 2;
      ctx.shadowColor = '#ffd700';
      ctx.shadowBlur = alpha * 8;
      ctx.beginPath();
      ctx.arc(0, 0, 22, angle, angle + 0.35);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    ctx.restore();

    // Loading progress bar with glow
    const progress = this.assetLoader.getProgress();
    const barW = 280;
    const barH = 8;
    const barX = cx - barW / 2;
    const barY = cy + 45;

    // Bar background
    ctx.fillStyle = '#1a1a2a';
    ctx.beginPath();
    ctx.roundRect(barX - 1, barY - 1, barW + 2, barH + 2, 5);
    ctx.fill();

    ctx.fillStyle = '#111122';
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, 4);
    ctx.fill();

    if (progress > 0) {
      // Gradient fill
      const fillGrad = ctx.createLinearGradient(barX, barY, barX + barW * progress, barY);
      fillGrad.addColorStop(0, '#cc8800');
      fillGrad.addColorStop(1, '#ffd700');
      ctx.fillStyle = fillGrad;
      ctx.beginPath();
      ctx.roundRect(barX, barY, barW * progress, barH, 4);
      ctx.fill();

      // Shine effect on progress bar
      ctx.save();
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(barX, barY, barW * progress, barH / 2, 4);
      ctx.fill();
      ctx.restore();
    }

    // Percentage text
    ctx.font = 'bold 13px Arial';
    ctx.fillStyle = '#999';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`${t('game.loading')} ${Math.floor(progress * 100)}%`, cx, barY + 14);

    // Animated shine sweep across progress bar
    if (progress > 0.05) {
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(barX, barY, barW * progress, barH, 4);
      ctx.clip();
      const sweepPos = ((time * 0.5) % 2 - 0.5) * barW * 1.5;
      const sweepGrad = ctx.createLinearGradient(barX + sweepPos - 40, barY, barX + sweepPos + 40, barY);
      sweepGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
      sweepGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.2)');
      sweepGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = sweepGrad;
      ctx.fillRect(barX + sweepPos - 40, barY, 80, barH);
      ctx.restore();
    }

    // Rotating tip with crossfade
    const tips = [
      t('loadingTips.tip1'), t('loadingTips.tip2'), t('loadingTips.tip3'),
      t('loadingTips.tip4'), t('loadingTips.tip5'), t('loadingTips.tip6'),
      t('loadingTips.tip7'), t('loadingTips.tip8'), t('loadingTips.tip9'),
      t('loadingTips.tip10'), t('loadingTips.tip11'), t('loadingTips.tip12'),
    ];
    const tip = tips[this.loadingTipIndex % tips.length];
    ctx.save();
    ctx.globalAlpha = this.loadingTipFade * 0.7;
    ctx.font = '13px Arial';
    ctx.fillStyle = '#887755';
    ctx.fillText('\u{1F4A1} ' + tip, cx, barY + 40);
    ctx.restore();
  }

  private renderMenu(): void {
    this.mainMenuUI.render(this.renderer);
  }

  private renderGame(): void {
    if (this.world) {
      this.world.render(this.renderer);

      // Render portals (in world coordinates)
      if (this.townPortal && (this.townPortal.isTownPortalActive() || this.townPortal.isReturnPortalActive())) {
        const cameraPos = this.world.camera.getPosition();
        this.townPortal.render(this.renderer.context, cameraPos.x, cameraPos.y);
      }
    }
  }

  private renderPauseOverlay(): void {
    const ctx = this.renderer.context;
    const w = this.canvas.width;
    const h = this.canvas.height;

    // Darken screen
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, w, h);

    if (this.pauseSettingsOpen) {
      this.renderPauseSettingsPanel(ctx, w, h);
    } else {
      // Pause text
      this.renderer.drawText(
        t('game.paused'),
        w / 2,
        h / 2 - 30,
        { color: COLORS.TEXT, fontSize: 48, align: 'center', baseline: 'middle' }
      );

      this.renderer.drawText(
        t('ui.resumeHint'),
        w / 2,
        h / 2 + 25,
        { color: '#888888', fontSize: 18, align: 'center', baseline: 'middle' }
      );

      // Settings button
      const btnW = 160;
      const btnH = 40;
      const btnX = w / 2 - btnW / 2;
      const btnY = h / 2 + 60;

      const isHovered = this.mouseInRect(btnX, btnY, btnW, btnH);
      ctx.fillStyle = isHovered ? 'rgba(60, 50, 20, 0.9)' : 'rgba(30, 30, 50, 0.8)';
      ctx.beginPath();
      ctx.roundRect(btnX, btnY, btnW, btnH, 6);
      ctx.fill();
      ctx.strokeStyle = isHovered ? '#ffd700' : '#555';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = isHovered ? '#ffd700' : '#ccc';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(t('ui.settings'), btnX + btnW / 2, btnY + btnH / 2);
    }
  }

  private mouseInRect(rx: number, ry: number, rw: number, rh: number): boolean {
    const mousePos = this.inputManager.getMousePosition();
    return mousePos.x >= rx && mousePos.x <= rx + rw && mousePos.y >= ry && mousePos.y <= ry + rh;
  }

  /** Render a simplified settings panel during pause */
  private renderPauseSettingsPanel(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const panelW = 500;
    const panelH = 380;
    const px = w / 2 - panelW / 2;
    const py = h / 2 - panelH / 2;

    // Panel background
    ctx.fillStyle = 'rgba(10, 10, 20, 0.92)';
    ctx.beginPath();
    ctx.roundRect(px, py, panelW, panelH, 10);
    ctx.fill();
    ctx.strokeStyle = '#3a3a5a';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Title
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 24px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('ui.settings'), w / 2, py + 30);

    // Draw audio sliders
    const audio = getAudioManager();
    const sliders = [
      { label: t('settings.masterVolume'), value: audio.getMasterVolume() },
      { label: t('settings.musicVolume'), value: audio.getMusicVolume() },
      { label: t('settings.sfxVolume'), value: audio.getSfxVolume() },
    ];

    const sliderX = px + 40;
    const sliderW = panelW - 80;

    for (let i = 0; i < sliders.length; i++) {
      const slider = sliders[i];
      const sy = py + 65 + i * 65;

      ctx.fillStyle = '#ccc';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(slider.label, sliderX, sy);

      ctx.textAlign = 'right';
      ctx.fillStyle = '#ffd700';
      ctx.fillText(`${Math.round(slider.value * 100)}%`, sliderX + sliderW, sy);

      const trackY = sy + 18;
      const trackH = 14;
      ctx.fillStyle = 'rgba(30, 30, 50, 0.8)';
      ctx.beginPath();
      ctx.roundRect(sliderX, trackY, sliderW, trackH, 4);
      ctx.fill();
      ctx.strokeStyle = '#3a3a5a';
      ctx.lineWidth = 1;
      ctx.stroke();

      const fillW = slider.value * sliderW;
      if (fillW > 0) {
        ctx.fillStyle = '#d4a84b';
        ctx.beginPath();
        ctx.roundRect(sliderX, trackY, fillW, trackH, 4);
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(sliderX + fillW, trackY + trackH / 2, 7, 0, Math.PI * 2);
      ctx.fillStyle = '#e0d0a0';
      ctx.fill();
      ctx.strokeStyle = '#ffd700';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Language toggle row
    const langY = py + 65 + 3 * 65;
    const currentLang = getCurrentLocale();

    ctx.fillStyle = '#888';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('settings.currentLanguage') + ':', sliderX, langY);

    const langBtnW = 80;
    const langBtnH = 30;
    const langBtnY = langY - langBtnH / 2;
    const trBtnX = sliderX + 180;
    const enBtnX = trBtnX + langBtnW + 10;

    for (const lang of ['tr', 'en'] as const) {
      const bx = lang === 'tr' ? trBtnX : enBtnX;
      const isActive = currentLang === lang;
      const isHov = this.mouseInRect(bx, langBtnY, langBtnW, langBtnH);

      ctx.fillStyle = isActive ? 'rgba(60, 50, 20, 0.9)' : isHov ? 'rgba(40, 40, 60, 0.8)' : 'rgba(25, 25, 40, 0.7)';
      ctx.beginPath();
      ctx.roundRect(bx, langBtnY, langBtnW, langBtnH, 5);
      ctx.fill();
      ctx.strokeStyle = isActive ? '#ffd700' : '#3a3a5a';
      ctx.lineWidth = isActive ? 2 : 1;
      ctx.stroke();

      ctx.fillStyle = isActive ? '#ffd700' : '#ccc';
      ctx.font = `${isActive ? 'bold ' : ''}13px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(lang === 'tr' ? 'TR' : 'EN', bx + langBtnW / 2, langBtnY + langBtnH / 2);
    }

    // Back button
    const backW = 100;
    const backH = 35;
    const backX = w / 2 - backW / 2;
    const backY = py + panelH - 50;
    const backHovered = this.mouseInRect(backX, backY, backW, backH);

    ctx.fillStyle = backHovered ? '#2a2a35' : '#1a1a25';
    ctx.beginPath();
    ctx.roundRect(backX, backY, backW, backH, 5);
    ctx.fill();
    ctx.strokeStyle = backHovered ? '#555' : '#333';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = backHovered ? '#fff' : '#888';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('ui.back'), backX + backW / 2, backY + backH / 2);
  }

  /** Get pause settings panel layout constants */
  private getPauseSettingsLayout(): { px: number; py: number; panelW: number; panelH: number; sliderX: number; sliderW: number } {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const panelW = 500;
    const panelH = 380;
    const px = w / 2 - panelW / 2;
    const py = h / 2 - panelH / 2;
    const sliderX = px + 40;
    const sliderW = panelW - 80;
    return { px, py, panelW, panelH, sliderX, sliderW };
  }

  private handlePauseClick(mx: number, my: number): void {
    const audio = getAudioManager();
    const w = this.canvas.width;
    const h = this.canvas.height;

    if (!this.pauseSettingsOpen) {
      // Check settings button click
      const btnW = 160;
      const btnH = 40;
      const btnX = w / 2 - btnW / 2;
      const btnY = h / 2 + 60;
      if (mx >= btnX && mx <= btnX + btnW && my >= btnY && my <= btnY + btnH) {
        audio.play('ui_click');
        this.pauseSettingsOpen = true;
      }
    } else {
      const layout = this.getPauseSettingsLayout();

      // Language buttons
      const langY = layout.py + 65 + 3 * 65;
      const langBtnW = 80;
      const langBtnH = 30;
      const langBtnY = langY - langBtnH / 2;
      const trBtnX = layout.sliderX + 180;
      const enBtnX = trBtnX + langBtnW + 10;

      if (mx >= trBtnX && mx <= trBtnX + langBtnW && my >= langBtnY && my <= langBtnY + langBtnH) {
        audio.play('ui_click');
        setLocale('tr');
        this.persistPauseSettings();
        return;
      }
      if (mx >= enBtnX && mx <= enBtnX + langBtnW && my >= langBtnY && my <= langBtnY + langBtnH) {
        audio.play('ui_click');
        setLocale('en');
        this.persistPauseSettings();
        return;
      }

      // Back button
      const backW = 100;
      const backH = 35;
      const backX = w / 2 - backW / 2;
      const backY = layout.py + layout.panelH - 50;
      if (mx >= backX && mx <= backX + backW && my >= backY && my <= backY + backH) {
        audio.play('ui_close');
        this.pauseSettingsOpen = false;
        return;
      }
    }
  }

  private getPauseSliderAtPosition(mx: number, my: number): number | null {
    const { py, sliderX, sliderW } = this.getPauseSettingsLayout();
    for (let i = 0; i < 3; i++) {
      const sy = py + 65 + i * 65;
      const trackY = sy + 18;
      const trackH = 14;
      if (mx >= sliderX - 5 && mx <= sliderX + sliderW + 5 && my >= trackY - 10 && my <= trackY + trackH + 10) {
        return i;
      }
    }
    return null;
  }

  private applyPauseSliderDrag(mx: number): void {
    if (this.pauseSliderDrag === null) return;
    const { sliderX, sliderW } = this.getPauseSettingsLayout();
    const ratio = Math.max(0, Math.min(1, (mx - sliderX) / sliderW));
    const audio = getAudioManager();

    switch (this.pauseSliderDrag) {
      case 0: audio.setMasterVolume(ratio); break;
      case 1: audio.setMusicVolume(ratio); break;
      case 2: audio.setSfxVolume(ratio); break;
    }
  }

  private persistPauseSettings(): void {
    const audio = getAudioManager();
    try {
      localStorage.setItem('arpg_settings', JSON.stringify({
        masterVolume: audio.getMasterVolume(),
        musicVolume: audio.getMusicVolume(),
        sfxVolume: audio.getSfxVolume(),
        locale: getCurrentLocale(),
      }));
    } catch { /* ignore */ }
  }

  private renderDeathOverlay(): void {
    const ctx = this.renderer.context;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const cx = w / 2;
    const cy = h / 2;
    const fade = this.deathFadeIn; // 0 -> 1
    const now = Date.now() / 1000;

    // Dark red vignette effect (fades in dramatically)
    ctx.save();
    ctx.globalAlpha = fade;

    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) / 2);
    gradient.addColorStop(0, 'rgba(50, 0, 0, 0.75)');
    gradient.addColorStop(0.6, 'rgba(30, 0, 0, 0.9)');
    gradient.addColorStop(1, 'rgba(10, 0, 0, 0.97)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    // Falling ash/ember particles
    ctx.save();
    for (let i = 0; i < 30; i++) {
      const seed = i * 137.5;
      const px = ((seed * 7.3 + now * 15 * (0.5 + (i % 5) * 0.2)) % w);
      const py = ((seed * 3.7 + now * 30 * (0.3 + (i % 3) * 0.15)) % h);
      const size = 1 + (i % 3) * 0.5;
      const alpha = 0.2 + Math.sin(now * 2 + i) * 0.1;
      ctx.globalAlpha = alpha * fade;
      ctx.fillStyle = i % 4 === 0 ? '#ff4422' : '#aa4433';
      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // Skull icon with glow and breathing animation
    const skullY = cy - 110;
    const breathScale = 1 + Math.sin(now * 1.5) * 0.03;
    ctx.save();
    ctx.translate(cx, skullY);
    ctx.scale(breathScale, breathScale);
    ctx.strokeStyle = '#aa2222';
    ctx.lineWidth = 2.5;
    ctx.fillStyle = '#aa2222';
    ctx.shadowColor = '#ff2222';
    ctx.shadowBlur = 8 + Math.sin(now * 2) * 4;

    // Skull circle
    ctx.beginPath();
    ctx.arc(0, 0, 22, 0, Math.PI * 2);
    ctx.stroke();
    // Eyes (glowing)
    ctx.fillStyle = '#ff3333';
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.arc(-8, -3, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(8, -3, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    // Nose
    ctx.fillStyle = '#aa2222';
    ctx.beginPath();
    ctx.moveTo(0, 4);
    ctx.lineTo(-3, 9);
    ctx.lineTo(3, 9);
    ctx.closePath();
    ctx.fill();
    // Jaw with teeth
    ctx.beginPath();
    ctx.moveTo(-14, 14);
    ctx.lineTo(14, 14);
    ctx.stroke();
    for (let i = -10; i <= 10; i += 5) {
      ctx.beginPath();
      ctx.moveTo(i, 14);
      ctx.lineTo(i, 18);
      ctx.stroke();
    }
    ctx.restore();

    // Sequential fade: elements appear one by one
    const titleFade = Math.min(1, fade * 2);                         // appears first
    const causeFade = Math.max(0, Math.min(1, (fade - 0.2) * 3));   // appears at 20%
    const subtitleFade = Math.max(0, Math.min(1, (fade - 0.35) * 3)); // appears at 35%
    const statsFade = Math.max(0, Math.min(1, (fade - 0.5) * 3));   // appears at 50%
    const penaltyFade = Math.max(0, Math.min(1, (fade - 0.6) * 3)); // appears at 60%
    const btnFade = Math.max(0, Math.min(1, (fade - 0.7) * 3));     // appears at 70%

    // Death title (slides up slightly)
    const titleOffset = (1 - titleFade) * 30;
    ctx.save();
    ctx.globalAlpha = titleFade;
    ctx.font = 'bold 56px Arial';
    ctx.fillStyle = '#aa2222';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#ff0000';
    ctx.shadowBlur = 10 + Math.sin(now * 3) * 3;
    ctx.fillText(t('game.death.title'), cx, cy - 50 + titleOffset);
    ctx.shadowBlur = 0;
    ctx.restore();

    // Cause of death text
    if (this.deathCauseText) {
      ctx.save();
      ctx.globalAlpha = causeFade;
      ctx.font = 'italic 18px Arial';
      ctx.fillStyle = '#cc6644';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.deathCauseText, cx, cy - 5 + (1 - causeFade) * 10);
      ctx.restore();
    }

    // Subtitle
    ctx.save();
    ctx.globalAlpha = subtitleFade;
    ctx.font = '20px Arial';
    ctx.fillStyle = '#882222';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('game.death.subtitle'), cx, cy + 20 + (1 - subtitleFade) * 15);
    ctx.restore();

    // Stats card (boxed)
    if (this.world) {
      ctx.save();
      ctx.globalAlpha = statsFade;
      const player = this.world.player;
      const statText = t('game.death.stats', { level: player.level, kills: this.world.totalKills });

      const cardW = 260;
      const cardH = this.deathGoldLost > 0 ? 60 : 44;
      const cardX = cx - cardW / 2;
      const cardY = cy + 45;

      // Card background with gradient
      const cardGrad = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY);
      cardGrad.addColorStop(0, 'rgba(30, 5, 5, 0.6)');
      cardGrad.addColorStop(0.5, 'rgba(40, 8, 8, 0.7)');
      cardGrad.addColorStop(1, 'rgba(30, 5, 5, 0.6)');
      ctx.fillStyle = cardGrad;
      ctx.beginPath();
      ctx.roundRect(cardX, cardY, cardW, cardH, 6);
      ctx.fill();
      ctx.strokeStyle = '#552222';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Stats text
      ctx.font = '14px Arial';
      ctx.fillStyle = '#bbaaaa';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const statsTextY = this.deathGoldLost > 0 ? cardY + cardH / 2 - 10 : cardY + cardH / 2;
      ctx.fillText(statText, cx, statsTextY);

      // Gold penalty line (inside the card)
      if (this.deathGoldLost > 0) {
        ctx.save();
        ctx.globalAlpha = penaltyFade;
        ctx.font = 'bold 13px Arial';
        ctx.fillStyle = '#cc8833';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(t('game.death.goldPenalty', { amount: this.deathGoldLost }), cx, cardY + cardH / 2 + 10);
        ctx.restore();
      }

      ctx.restore();
    }

    // Respawn countdown or button
    ctx.save();
    ctx.globalAlpha = btnFade;
    const btnY = cy + 125;
    const btnW = 260;
    const btnH = 44;

    if (!this.deathCountdownReady) {
      // Countdown timer - show remaining seconds
      const seconds = Math.ceil(this.deathCountdown);
      const countdownText = t('game.death.countdown', { seconds: seconds });

      // Countdown bar background
      ctx.fillStyle = 'rgba(60, 10, 10, 0.5)';
      ctx.beginPath();
      ctx.roundRect(cx - btnW / 2, btnY, btnW, btnH, 8);
      ctx.fill();
      ctx.strokeStyle = 'rgba(100, 30, 30, 0.6)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Progress bar fill (shrinks as countdown decreases)
      const progress = this.deathCountdown / 5;
      const barPad = 4;
      const barW = (btnW - barPad * 2) * progress;
      ctx.fillStyle = 'rgba(120, 25, 25, 0.5)';
      ctx.beginPath();
      ctx.roundRect(cx - btnW / 2 + barPad, btnY + barPad, barW, btnH - barPad * 2, 5);
      ctx.fill();

      // Countdown text
      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = '#aa8888';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(countdownText, cx, btnY + btnH / 2);
    } else {
      // Return to town button (pulsing, ready to click)
      const pulse = Math.sin(now * 1.5) * 0.25 + 0.75;
      const btnScale = 1 + (pulse - 0.75) * 0.02;

      ctx.translate(cx, btnY + btnH / 2);
      ctx.scale(btnScale, btnScale);
      ctx.translate(-cx, -(btnY + btnH / 2));

      ctx.fillStyle = `rgba(80, 40, 15, ${0.3 + pulse * 0.2})`;
      ctx.beginPath();
      ctx.roundRect(cx - btnW / 2, btnY, btnW, btnH, 8);
      ctx.fill();
      ctx.strokeStyle = `rgba(180, 120, 50, ${pulse})`;
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = `rgba(220, 180, 120, ${pulse})`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(t('game.death.returnToTown'), cx, btnY + btnH / 2 - 8);

      // Hint text below button text
      ctx.font = '12px Arial';
      ctx.fillStyle = `rgba(170, 130, 100, ${pulse * 0.7})`;
      ctx.fillText(t('game.death.respawnHint'), cx, btnY + btnH / 2 + 10);
    }
    ctx.restore();

    ctx.restore();
  }

  private respawnInTown(): void {
    if (!this.world) return;

    const player = this.world.player;

    // Restore HP to 50% and mana to full
    player.stats.hp = Math.floor(player.stats.maxHp * 0.5);
    player.stats.mana = player.stats.maxMana;

    // Revive the player
    player.isAlive = true;
    player.lastDamageSourceName = '';

    // Clear debuffs and status effects
    player.clearDebuffs();

    // Reset camera zoom
    this.world.camera.zoomTo(1, 0.5);

    // Check if player is already in otag_koyu
    const currentZone = this.world.getCurrentZoneId();
    if (currentZone === 'otag_koyu') {
      // Already in town - just move to waypoint
      const zoneConfig = this.world.getCurrentZone();
      if (zoneConfig.waypointPosition) {
        player.position.x = zoneConfig.waypointPosition.x;
        player.position.y = zoneConfig.waypointPosition.y;
      } else {
        const mapCenter = this.world.map.getCenter();
        player.position.x = mapCenter.x;
        player.position.y = mapCenter.y;
      }

      // Clear nearby enemies
      this.world.enemies = this.world.enemies.filter(enemy => {
        const dx = enemy.position.x - player.position.x;
        const dy = enemy.position.y - player.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        return dist > 300;
      });

      // Reset camera target
      this.world.camera.setTarget(player.position);

      this.state = 'playing';
      this.audioManager.play('ui_confirm');
      debugLog('Game', 'Player respawned in town (50% HP, gold penalty applied)');
    } else {
      // Transition to otag_koyu (safe zone)
      this.state = 'playing';

      // Clear stored zone state (don't allow return portal after death)
      this.storedZoneState = null;
      this.isReturningViaPortal = false;
      this.pendingReturnPortal = null;

      // Start zone transition to safe zone
      this.startZoneTransition('otag_koyu' as ZoneType, true);

      this.audioManager.play('ui_confirm');
      debugLog('Game', 'Player respawning to Otag Koyu (50% HP, gold penalty applied)');
    }
  }

  private renderDebugInfo(): void {
    // FPS and locale are now shown in the HTML HUD bottom panel
    if (this.hudHTML?.isShown()) {
      this.hudHTML.setFPS(this.gameLoop.getFPS());
      this.hudHTML.setLocale(getCurrentLocale());
    }
  }

  // ==========================================
  // DEMO COMPLETE SCREEN
  // ==========================================

  private renderDemoCompleteScreen(): void {
    const ctx = this.renderer.context;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const alpha = this.demoFadeIn;

    ctx.save();
    ctx.globalAlpha = alpha;

    // Dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.fillRect(0, 0, w, h);

    // Decorative gradient glow at center
    const glow = ctx.createRadialGradient(w / 2, h / 2 - 60, 0, w / 2, h / 2 - 60, 300);
    glow.addColorStop(0, 'rgba(255, 215, 0, 0.08)');
    glow.addColorStop(1, 'rgba(255, 215, 0, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    // Title
    ctx.font = 'bold 48px serif';
    ctx.fillStyle = '#ffd700';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#ff8800';
    ctx.shadowBlur = 20;
    ctx.fillText(t('demoComplete.title'), w / 2, h / 2 - 150);
    ctx.shadowBlur = 0;

    // Subtitle
    ctx.font = '20px sans-serif';
    ctx.fillStyle = '#ccaa66';
    ctx.fillText(t('demoComplete.subtitle'), w / 2, h / 2 - 110);

    // Decorative line
    const lineW = 180;
    const lineGrad = ctx.createLinearGradient(w / 2 - lineW, 0, w / 2 + lineW, 0);
    lineGrad.addColorStop(0, 'rgba(255, 215, 0, 0)');
    lineGrad.addColorStop(0.3, 'rgba(255, 215, 0, 0.6)');
    lineGrad.addColorStop(0.7, 'rgba(255, 215, 0, 0.6)');
    lineGrad.addColorStop(1, 'rgba(255, 215, 0, 0)');
    ctx.fillStyle = lineGrad;
    ctx.fillRect(w / 2 - lineW, h / 2 - 90, lineW * 2, 2);

    // Play time
    const totalSeconds = Math.floor(this.playTime);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const timeStr = hours > 0
      ? `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`
      : `${minutes}m ${seconds.toString().padStart(2, '0')}s`;

    ctx.font = '18px sans-serif';
    ctx.fillStyle = '#aaaaaa';
    ctx.fillText(`${t('demoComplete.playTime')}: ${timeStr}`, w / 2, h / 2 - 60);

    // Stats
    const stats: string[] = [];
    if (this.world) {
      stats.push(`${t('demoComplete.level')}: ${this.world.player.level}`);
      stats.push(`${t('demoComplete.totalKills')}: ${this.world.totalKills}`);
      stats.push(`${t('demoComplete.bossDefeated')}: ${this.world.bossesDefeated}`);
    }

    ctx.font = '16px sans-serif';
    ctx.fillStyle = '#999999';
    let statY = h / 2 - 25;
    for (const stat of stats) {
      ctx.fillText(stat, w / 2, statY);
      statY += 26;
    }

    // Thank you message
    ctx.font = 'italic 16px sans-serif';
    ctx.fillStyle = '#887755';
    ctx.fillText(t('demoComplete.thanks'), w / 2, statY + 15);

    // Button layout
    const btnW = 240;
    const btnH = 48;
    const btnGap = 20;
    const btnX = w / 2 - btnW / 2;
    const btnY1 = h / 2 + 80;
    const btnY2 = btnY1 + btnH + btnGap;
    const mx = this.inputManager.getMousePosition().x;
    const my = this.inputManager.getMousePosition().y;

    // "Wishlist on Steam" button (with hover)
    const hover1 = mx >= btnX && mx <= btnX + btnW && my >= btnY1 && my <= btnY1 + btnH;
    this.renderDemoButton(ctx, btnX, btnY1, btnW, btnH,
      t('demoComplete.wishlist'), '#ffd700', hover1 ? '#5a4a20' : '#3a2a10', hover1);

    // "Return to Main Menu" button (with hover)
    const hover2 = mx >= btnX && mx <= btnX + btnW && my >= btnY2 && my <= btnY2 + btnH;
    this.renderDemoButton(ctx, btnX, btnY2, btnW, btnH,
      t('demoComplete.returnToMenu'), '#aaaaaa', hover2 ? '#3a3a4a' : '#2a2a3a', hover2);

    ctx.restore();
  }

  private renderDemoButton(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number,
    text: string, textColor: string, bgColor: string, hovered: boolean = false
  ): void {
    // Button background
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 8);
    ctx.fill();

    // Button border (brighter on hover)
    ctx.strokeStyle = hovered ? '#ffffff' : textColor;
    ctx.lineWidth = hovered ? 3 : 2;
    ctx.stroke();

    // Hover glow
    if (hovered) {
      ctx.shadowColor = textColor;
      ctx.shadowBlur = 12;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // Button text
    ctx.font = `bold ${hovered ? 19 : 18}px sans-serif`;
    ctx.fillStyle = hovered ? '#ffffff' : textColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x + w / 2, y + h / 2);
  }

  private handleDemoCompleteClick(mx: number, my: number): void {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const btnW = 240;
    const btnH = 48;
    const btnGap = 20;
    const btnX = w / 2 - btnW / 2;
    const btnY1 = h / 2 + 80;
    const btnY2 = btnY1 + btnH + btnGap;

    // "Wishlist on Steam" button
    if (mx >= btnX && mx <= btnX + btnW && my >= btnY1 && my <= btnY1 + btnH) {
      // Open Steam store page (placeholder URL)
      window.open('https://store.steampowered.com', '_blank');
      this.audioManager.play('ui_confirm');
    }

    // "Return to Main Menu" button
    if (mx >= btnX && mx <= btnX + btnW && my >= btnY2 && my <= btnY2 + btnH) {
      this.demoComplete = false;
      this.state = 'menu';
      this.world = null;
      this.audioManager.playMusic('music_menu');
      this.audioManager.play('ui_confirm');
    }
  }

  private renderAutoSaveIndicator(): void {
    if (!this.isAutoSaving) return;

    const ctx = this.renderer.context;
    const x = 20;
    const y = this.canvas.height - 30;

    ctx.save();

    // Fade out effect
    const alpha = Math.min(1, this.autoSaveIndicatorTimer);
    ctx.globalAlpha = alpha;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.beginPath();
    ctx.roundRect(x - 5, y - 12, 130, 24, 5);
    ctx.fill();

    // Spinning save icon
    const iconX = x + 10;
    const iconY = y;
    const rotation = Date.now() / 200;

    ctx.save();
    ctx.translate(iconX, iconY);
    ctx.rotate(rotation);

    // Draw circular arrows (save icon)
    ctx.strokeStyle = '#4ade80';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, 6, 0, Math.PI * 1.5);
    ctx.stroke();

    // Arrow head
    ctx.beginPath();
    ctx.moveTo(0, -6);
    ctx.lineTo(3, -3);
    ctx.lineTo(-3, -3);
    ctx.closePath();
    ctx.fillStyle = '#4ade80';
    ctx.fill();

    ctx.restore();

    // Text
    ctx.font = '14px Arial';
    ctx.fillStyle = '#4ade80';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('ui.saving'), x + 25, y);

    ctx.restore();
  }

  setLanguage(locale: 'tr' | 'en'): void {
    setLocale(locale);
    debugLog('Game',` Language changed to: ${locale}`);
  }

  private renderErrorOverlay(error: { message: string; stack: string }): void {
    const ctx = this.renderer.context;
    const w = this.canvas.width;
    const h = this.canvas.height;

    // Dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.fillRect(0, 0, w, h);

    // Error box
    const boxW = Math.min(600, w - 40);
    const boxH = 280;
    const boxX = (w - boxW) / 2;
    const boxY = (h - boxH) / 2;

    ctx.fillStyle = '#1a0a0a';
    ctx.strokeStyle = '#ff3333';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxW, boxH, 8);
    ctx.fill();
    ctx.stroke();

    // Title
    ctx.fillStyle = '#ff4444';
    ctx.font = 'bold 22px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('⚠ ' + t('ui.errorTitle'), w / 2, boxY + 35);

    // Error message
    ctx.fillStyle = '#cc8888';
    ctx.font = '14px monospace';
    ctx.textAlign = 'left';
    const maxChars = Math.floor((boxW - 40) / 8);
    const msg = error.message.length > maxChars ? error.message.substring(0, maxChars) + '...' : error.message;
    ctx.fillText(msg, boxX + 20, boxY + 75);

    // Stack (first 5 lines)
    ctx.fillStyle = '#886666';
    ctx.font = '11px monospace';
    const lines = error.stack.split('\n').slice(0, 5);
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim().substring(0, maxChars + 10);
      ctx.fillText(line, boxX + 20, boxY + 105 + i * 16);
    }

    // Auto-save notice
    ctx.fillStyle = '#88aa88';
    ctx.font = '13px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(t('ui.autoSaved'), w / 2, boxY + 215);

    // Reload hint
    ctx.fillStyle = '#aaaaaa';
    ctx.font = '14px Arial';
    ctx.fillText(t('ui.reloadHint'), w / 2, boxY + 250);
  }
}
